import { Resource, Booking, Project, AppSettings, Client, CostCentre, ProjectDetails, Absence, BankHoliday, BookingAuditEntry } from '@/types';
import { getProjectColor } from './utils/calendar';
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import { generateLegacyFieldConfigurations } from '@/utils/field-configurations-legacy';

// Mock data for Resource Scheduler

export const mockResources: Resource[] = [
  // Special system resource for unallocated bookings
  {
    id: 'unallocated',
    name: 'Unallocated',
    role: '', // No role for system resource
    status: 'available',
    type: 'permanent',
    photoUrl: undefined, // No photo for system resource
    department: 'System', // Separate department for system resources
    schedulerGroup: 'System', // Separate group for system resources
  },
  {
    id: '1',
    name: 'Sarah Johnson',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=1',
    email: 'sarah.johnson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'New York',
    skills: [{ name: 'React', rating: 5 }, { name: 'TypeScript', rating: 4 }, { name: 'Node.js', rating: 4 }],
    level: 'Senior',
  },
  {
    id: '2',
    name: 'Michael Chen',
    role: 'UI/UX Designer',
    status: 'busy',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=12',
    email: 'michael.chen@company.com',
    department: 'Design',
    schedulerGroup: 'Team Beta',
    location: 'San Francisco',
    skills: [{ name: 'Figma', rating: 5 }, { name: 'Adobe XD', rating: 4 }, { name: 'User Research', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    role: 'Project Manager',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=5',
    email: 'emily.rodriguez@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Alpha',
    location: 'Austin',
    skills: [{ name: 'Agile', rating: 5 }, { name: 'Scrum', rating: 4 }, { name: 'Stakeholder Management', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '4',
    name: 'David Kim',
    role: 'Backend Developer',
    status: 'active',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=13',
    email: 'david.kim@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Seattle',
    skills: [{ name: 'Python', rating: 5 }, { name: 'Django', rating: 4 }, { name: 'PostgreSQL', rating: 4 }],
    level: 'Mid-Level',
    useCustomWorkingProfile: true,
    customWorkingProfile: {
      monday: 7.5,
      tuesday: 7.5,
      wednesday: 7.5,
      thursday: 7.5,
      friday: 0,
      saturday: 0,
      sunday: 0,
    },
  },
  {
    id: '5',
    name: 'Jessica Williams',
    role: 'QA Engineer',
    status: 'available',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=9',
    email: 'jessica.williams@company.com',
    department: 'Quality Assurance',
    schedulerGroup: 'Team Gamma',
    location: 'Boston',
    skills: [{ name: 'Test Automation', rating: 5 }, { name: 'Selenium', rating: 4 }, { name: 'Jest', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '6',
    name: 'Robert Taylor',
    role: 'DevOps Engineer',
    status: 'busy',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=14',
    email: 'robert.taylor@company.com',
    department: 'Infrastructure',
    schedulerGroup: 'Team Beta',
    location: 'Chicago',
    skills: [{ name: 'Docker', rating: 5 }, { name: 'Kubernetes', rating: 4 }, { name: 'AWS', rating: 5 }],
    level: 'Senior',
  },
  {
    id: '7',
    name: 'Amanda Martinez',
    role: 'Frontend Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=20',
    email: 'amanda.martinez@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Beta',
    location: 'Miami',
    skills: [{ name: 'Vue.js', rating: 4 }, { name: 'CSS', rating: 5 }, { name: 'JavaScript', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '8',
    name: 'James Anderson',
    role: 'Data Analyst',
    status: 'available',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=33',
    email: 'james.anderson@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Gamma',
    location: 'Denver',
    skills: [{ name: 'SQL', rating: 5 }, { name: 'Python', rating: 4 }, { name: 'Tableau', rating: 4 }],
    level: 'Senior',
  },
  // Additional resources
  {
    id: '9',
    name: 'Lisa Thompson',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=25',
    email: 'lisa.thompson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Portland',
    skills: [{ name: 'Java', rating: 5 }, { name: 'Spring Boot', rating: 4 }, { name: 'Microservices', rating: 4 }],
    level: 'Senior',
  },
  {
    id: '10',
    name: 'Daniel Brown',
    role: 'UI/UX Designer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=52',
    email: 'daniel.brown@company.com',
    department: 'Design',
    schedulerGroup: 'Team Beta',
    location: 'Los Angeles',
    skills: [{ name: 'Sketch', rating: 4 }, { name: 'InVision', rating: 4 }, { name: 'Prototyping', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '11',
    name: 'Rachel Green',
    role: 'Business Analyst',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=32',
    email: 'rachel.green@company.com',
    department: 'Operations',
    schedulerGroup: 'Team Gamma',
    location: 'Atlanta',
    skills: [{ name: 'Requirements Analysis', rating: 5 }, { name: 'JIRA', rating: 4 }, { name: 'Process Mapping', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '12',
    name: 'Chris Wilson',
    role: 'Backend Developer',
    status: 'busy',
    type: 'contractor',
    photoUrl: 'https://i.pravatar.cc/150?img=57',
    email: 'chris.wilson@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'Remote',
    skills: [{ name: 'C#', rating: 5 }, { name: '.NET Core', rating: 4 }, { name: 'Azure', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '13',
    name: 'Sophie Turner',
    role: 'QA Engineer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=44',
    email: 'sophie.turner@company.com',
    department: 'Quality Assurance',
    schedulerGroup: 'Team Gamma',
    location: 'Philadelphia',
    skills: [{ name: 'Manual Testing', rating: 4 }, { name: 'API Testing', rating: 5 }, { name: 'Postman', rating: 4 }],
    level: 'Mid-Level',
  },
  {
    id: '14',
    name: 'Mark Davis',
    role: 'DevOps Engineer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=60',
    email: 'mark.davis@company.com',
    department: 'Infrastructure',
    schedulerGroup: 'Team Beta',
    location: 'Dallas',
    skills: [{ name: 'Jenkins', rating: 5 }, { name: 'Terraform', rating: 4 }, { name: 'CI/CD', rating: 5 }],
    level: 'Senior',
  },
  {
    id: '15',
    name: 'Emma White',
    role: 'Frontend Developer',
    status: 'available',
    type: 'agency',
    photoUrl: 'https://i.pravatar.cc/150?img=47',
    email: 'emma.white@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Beta',
    location: 'Phoenix',
    skills: [{ name: 'Angular', rating: 4 }, { name: 'RxJS', rating: 4 }, { name: 'SCSS', rating: 5 }],
    level: 'Mid-Level',
  },
  {
    id: '16',
    name: 'Alex Morgan',
    role: 'Senior Developer',
    status: 'available',
    type: 'permanent',
    photoUrl: 'https://i.pravatar.cc/150?img=68',
    email: 'alex.morgan@company.com',
    department: 'Engineering',
    schedulerGroup: 'Team Alpha',
    location: 'London',
    skills: [{ name: 'React', rating: 5 }, { name: 'TypeScript', rating: 5 }, { name: 'Node.js', rating: 4 }],
    level: 'Senior',
  },
];

// Generate some sample bookings across multiple weeks
const today = new Date();
today.setHours(0, 0, 0, 0);

// Returns the number of days from today to Monday of the current week (negative = past).
// Monday = 1, so: Sun=0→-6, Mon=1→0, Tue=2→-1, Wed=3→-2, Thu=4→-3, Fri=5→-4, Sat=6→-5
const getMondayOffset = (): number => {
  const dow = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  return dow === 0 ? -6 : 1 - dow;
};

// Helper function to get a date with time offset from today
// This allows bookings to span multiple weeks so they're visible regardless of which week is viewed
// Returns ISO-like string WITHOUT "Z" suffix to prevent UTC interpretation and timezone offset
const getDateTimeOffset = (days: number, hours: number, minutes: number = 0): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  date.setHours(hours, minutes, 0, 0);
  // Format as local ISO string without Z suffix to prevent timezone conversion
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${h}:${m}:00.000`;
};

// Helper function for full-day bookings (12:00 AM to 12:00 AM next day)
const getFullDayStart = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

const getFullDayEnd = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days + 1); // Next day at midnight
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

// Helper function for multi-day full-day bookings
const getMultiDayEnd = (startDays: number, durationDays: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + startDays + durationDays);
  date.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00.000`;
};

// Helper to produce an ISO timestamp offset from today (for audit history)
const getAuditDate = (daysAgo: number, hours = 9, minutes = 0): string => {
  const date = new Date(today);
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// ─── Shared audit personas ────────────────────────────────────────────────────
const AUDIT_USERS = {
  sarah:   { performedBy: 'Sarah Johnson',   performedByInitials: 'SJ', performedByAvatar: 'https://i.pravatar.cc/150?img=1'  },
  michael: { performedBy: 'Michael Chen',    performedByInitials: 'MC', performedByAvatar: 'https://i.pravatar.cc/150?img=12' },
  emily:   { performedBy: 'Emily Rodriguez', performedByInitials: 'ER', performedByAvatar: 'https://i.pravatar.cc/150?img=5'  },
  david:   { performedBy: 'David Kim',       performedByInitials: 'DK', performedByAvatar: 'https://i.pravatar.cc/150?img=13' },
  admin:   { performedBy: 'James Fletcher',  performedByInitials: 'JF', performedByAvatar: 'https://i.pravatar.cc/150?img=53' },
};

// ─── Audit history blocks ─────────────────────────────────────────────────────

const auditHistory_b1: BookingAuditEntry[] = [
  {
    id: 'ah-b1-1',
    action: 'created',
    ...AUDIT_USERS.sarah,
    timestamp: getAuditDate(14, 9, 12),
    changes: [],
    note: 'Booking raised for Sprint 5 kick-off.',
  },
  {
    id: 'ah-b1-2',
    action: 'updated',
    ...AUDIT_USERS.sarah,
    timestamp: getAuditDate(12, 11, 45),
    changes: [
      { field: 'Location', oldValue: 'Remote', newValue: 'New York Office' },
      { field: 'Work Type', oldValue: 'Remote', newValue: 'On-site' },
    ],
  },
  {
    id: 'ah-b1-3',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(11, 14, 30),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
    note: 'Approved following client sign-off on scope.',
  },
  {
    id: 'ah-b1-4',
    action: 'updated',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(5, 10, 0),
    changes: [
      { field: 'Priority', oldValue: 'Medium', newValue: 'High' },
      { field: 'Details', oldValue: 'Sprint 5 - Focus on responsive design', newValue: 'Sprint 5 - Responsive design + accessibility pass' },
    ],
    note: 'Priority raised after client escalation.',
  },
];

const auditHistory_b2: BookingAuditEntry[] = [
  {
    id: 'ah-b2-1',
    action: 'created',
    ...AUDIT_USERS.michael,
    timestamp: getAuditDate(20, 10, 0),
    changes: [],
  },
  {
    id: 'ah-b2-2',
    action: 'rescheduled',
    ...AUDIT_USERS.michael,
    timestamp: getAuditDate(10, 9, 15),
    changes: [
      { field: 'Start Date', oldValue: getAuditDate(5, 9).slice(0, 10), newValue: getAuditDate(0, 9).slice(0, 10) },
      { field: 'End Date',   oldValue: getAuditDate(4, 17).slice(0, 10), newValue: getAuditDate(-1, 17).slice(0, 10) },
    ],
    note: 'Client requested earlier delivery of initial mockups.',
  },
  {
    id: 'ah-b2-3',
    action: 'resource_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(7, 13, 0),
    changes: [
      { field: 'Resource', oldValue: 'Jessica Williams', newValue: 'Michael Chen' },
    ],
    note: 'Reassigned following team restructure.',
  },
  {
    id: 'ah-b2-4',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(7, 15, 45),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
  },
];

const auditHistory_b3: BookingAuditEntry[] = [
  {
    id: 'ah-b3-1',
    action: 'created',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(18, 8, 30),
    changes: [],
    note: 'Q2 planning session — 3 day block reserved.',
  },
  {
    id: 'ah-b3-2',
    action: 'updated',
    ...AUDIT_USERS.emily,
    timestamp: getAuditDate(15, 14, 0),
    changes: [
      { field: 'Description', oldValue: 'Q2 roadmap planning', newValue: 'Q2 roadmap & OKR alignment' },
      { field: 'Location', oldValue: 'London Office', newValue: 'Zoom' },
    ],
  },
  {
    id: 'ah-b3-3',
    action: 'status_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(3, 9, 0),
    changes: [
      { field: 'Status', oldValue: 'Planned', newValue: 'Confirmed' },
    ],
  },
];

const auditHistory_b4: BookingAuditEntry[] = [
  {
    id: 'ah-b4-1',
    action: 'created',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(30, 11, 0),
    changes: [],
    note: 'Initial booking raised for API Integration phase 2.',
  },
  {
    id: 'ah-b4-2',
    action: 'updated',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(22, 10, 30),
    changes: [
      { field: 'Billing Classification', oldValue: 'Internal', newValue: 'Billable' },
      { field: 'Cost Centre', oldValue: '(none)', newValue: 'CC-001 Engineering' },
      { field: 'Customer', oldValue: '(none)', newValue: 'Global Finance Ltd' },
    ],
  },
  {
    id: 'ah-b4-3',
    action: 'resource_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(20, 14, 0),
    changes: [
      { field: 'Resource', oldValue: 'Sarah Johnson', newValue: 'David Kim' },
    ],
    note: 'Reassigned — Sarah has a scheduling conflict with the London sprint.',
  },
  {
    id: 'ah-b4-4',
    action: 'rescheduled',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(16, 15, 20),
    changes: [
      { field: 'Start Date', oldValue: getAuditDate(10, 9).slice(0, 10), newValue: getAuditDate(0, 9).slice(0, 10) },
      { field: 'End Date',   oldValue: getAuditDate(9, 17).slice(0, 10), newValue: getAuditDate(-1, 17).slice(0, 10) },
      { field: 'Duration',   oldValue: 'Full Day', newValue: 'Full Day' },
    ],
    note: 'Moved forward — dependency on auth service resolved early.',
  },
  {
    id: 'ah-b4-5',
    action: 'approved',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(15, 9, 0),
    changes: [
      { field: 'Status', oldValue: 'Pending Approval', newValue: 'Confirmed' },
    ],
    note: 'Approved following sign-off from Head of Engineering.',
  },
  {
    id: 'ah-b4-6',
    action: 'updated',
    ...AUDIT_USERS.david,
    timestamp: getAuditDate(8, 11, 15),
    changes: [
      { field: 'Details', oldValue: 'REST API endpoints development', newValue: 'REST API endpoints + OpenAPI spec documentation' },
      { field: 'Priority', oldValue: 'Medium', newValue: 'High' },
      { field: 'Location', oldValue: 'Remote', newValue: 'Manchester Office' },
    ],
    note: 'Priority raised after client escalation call this morning.',
  },
  {
    id: 'ah-b4-7',
    action: 'status_changed',
    ...AUDIT_USERS.admin,
    timestamp: getAuditDate(1, 8, 0),
    changes: [
      { field: 'Status', oldValue: 'Confirmed', newValue: 'In Progress' },
    ],
  },
];

export const mockBookings: Booking[] = [
  // ===== FULL-DAY BOOKINGS (12:00 AM to 12:00 AM) =====
  // Full-day booking with Project + Cost Centre
  {
    id: 'b1',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Full day development sprint',
    startTime: '2026-04-27T00:00:00.000',
    endTime: '2026-04-28T00:00:00.000',
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Sprint 5 - Focus on responsive design',
    auditHistory: auditHistory_b1,
  },
  // Full-day booking with Project only (no Cost Centre)
  {
    id: 'b2',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'UI mockups and prototypes',
    startTime: getFullDayStart(0),
    endTime: getFullDayEnd(0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design system implementation',
    auditHistory: auditHistory_b2,
  },
  // Multi-day full-day booking (3 days) with Project + Cost Centre
  {
    id: 'b3',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Q2 roadmap planning',
    startTime: getFullDayStart(1),
    endTime: getMultiDayEnd(1, 3),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    clientId: 'c7',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Team alignment and goal setting',
    auditHistory: auditHistory_b3,
  },
  // Full-day booking with Project only
  {
    id: 'b4',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Backend API development',
    startTime: getFullDayStart(0),
    endTime: getFullDayEnd(0),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'REST API endpoints development',
    auditHistory: auditHistory_b4,
  },

  // ===== HALF-DAY BOOKINGS =====
  // Half-day AM booking with Project + Cost Centre
  {
    id: 'b5',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Morning regression testing',
    startTime: getDateTimeOffset(0, 9, 0),
    endTime: getDateTimeOffset(0, 13, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    halfDayPeriod: 'am',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c5',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'End-to-end testing suite',
  },
  // Half-day PM booking with Project only
  {
    id: 'b6',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Afternoon cloud deployment',
    startTime: getDateTimeOffset(0, 13, 0),
    endTime: getDateTimeOffset(0, 17, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    halfDayPeriod: 'pm',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c6',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'AWS infrastructure setup',
  },
  // Half-day AM booking with Project + Cost Centre
  {
    id: 'b7',
    resourceId: '7',
    projectId: 'pd6',
    projectName: 'Component Library',
    costCentreId: 'cc1',
    description: 'Morning component development',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Component Library'),
    durationType: 'half-day',
    halfDayPeriod: 'am',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'React component development',
  },

  // ===== HOURLY BOOKINGS =====
  // Hourly booking with Project + Cost Centre
  {
    id: 'b8',
    resourceId: '8',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Data analysis review',
    startTime: getDateTimeOffset(0, 10, 0),
    endTime: getDateTimeOffset(0, 12, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    clientId: 'c7',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Quarterly metrics review',
  },
  // Hourly booking with Project only
  {
    id: 'b9',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Code review session',
    startTime: getDateTimeOffset(0, 14, 0),
    endTime: getDateTimeOffset(0, 16, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Pull request reviews',
  },
  // Hourly booking with Project + Cost Centre
  {
    id: 'b10',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc2',
    description: 'Design review meeting',
    startTime: getDateTimeOffset(0, 11, 0),
    endTime: getDateTimeOffset(0, 12, 30),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Stakeholder design review',
  },

  // ===== ADDITIONAL RESOURCES BOOKINGS =====
  // Full-day booking for new resource with Project + Cost Centre
  {
    id: 'b11',
    resourceId: '11',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Business requirements analysis',
    startTime: getFullDayStart(1),
    endTime: getFullDayEnd(1),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Manchester Office',
    details: 'Gathering API requirements',
  },
  // Half-day booking for new resource with Project only
  {
    id: 'b12',
    resourceId: '12',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Database migration planning',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    halfDayPeriod: 'am',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c6',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Planning database schema changes',
  },
  // Hourly booking for new resource with Project + Cost Centre
  {
    id: 'b13',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test case review',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 16, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c5',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Reviewing test coverage',
  },
  // Full-day booking for new resource with Project only
  {
    id: 'b14',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'CI/CD pipeline setup',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c6',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'GitHub Actions configuration',
  },
  // Half-day booking for new resource with Project + Cost Centre
  {
    id: 'b15',
    resourceId: '15',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Frontend bug fixes',
    startTime: getDateTimeOffset(2, 13, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    halfDayPeriod: 'pm',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Fixing reported UI issues',
  },

  // ===== WEEK 2 BOOKINGS (Days 7-13) =====
  // Full-day booking with Project + Cost Centre
  {
    id: 'b16',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Phase 2 development',
    startTime: getFullDayStart(7),
    endTime: getFullDayEnd(7),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Sprint 6 - Mobile responsiveness',
  },
  // Multi-day full-day booking with Project only
  {
    id: 'b17',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'User testing week',
    startTime: getFullDayStart(7),
    endTime: getMultiDayEnd(7, 5),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Implementing user feedback',
  },
  // Half-day booking with Project + Cost Centre
  {
    id: 'b18',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Performance testing',
    startTime: getDateTimeOffset(8, 9, 0),
    endTime: getDateTimeOffset(8, 13, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    halfDayPeriod: 'am',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c5',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Load and stress testing',
  },
  // Hourly booking with Project only
  {
    id: 'b19',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Monitoring setup',
    startTime: getDateTimeOffset(9, 10, 0),
    endTime: getDateTimeOffset(9, 12, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c6',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setting up CloudWatch alerts',
  },
  // Full-day booking with Project + Cost Centre
  {
    id: 'b20',
    resourceId: '9',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API documentation',
    startTime: getFullDayStart(8),
    endTime: getFullDayEnd(8),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Writing API documentation',
  },

  // ===== FEB 13 (Day 4) =====
  // Full-day booking with Project + Cost Centre
  {
    id: 'b21',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Homepage redesign',
    startTime: getFullDayStart(4),
    endTime: getFullDayEnd(4),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Landing page redesign sprint',
  },
  // Half-day booking with Project only
  {
    id: 'b22',
    resourceId: '3',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'App wireframes review',
    startTime: getDateTimeOffset(4, 9, 0),
    endTime: getDateTimeOffset(4, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Review wireframes with stakeholders',
  },
  // Hourly booking with Project + Cost Centre
  {
    id: 'b23',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test automation setup',
    startTime: getDateTimeOffset(4, 14, 0),
    endTime: getDateTimeOffset(4, 16, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c5',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Setting up Cypress tests',
  },

  // ===== FEB 14 (Day 5) =====
  // Full-day booking with Project only
  {
    id: 'b24',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Valentine special UI',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c3',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Special Valentine theme implementation',
  },
  // Half-day booking with Project + Cost Centre
  {
    id: 'b25',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API security review',
    startTime: getDateTimeOffset(5, 13, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c2',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Security audit for payment APIs',
  },
  // Hourly booking with Project only
  {
    id: 'b26',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'UI component review',
    startTime: getDateTimeOffset(5, 10, 0),
    endTime: getDateTimeOffset(5, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c4',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Review new component library',
  },

  // ===== FEB 15 (Day 6) =====
  // Full-day booking with Project + Cost Centre
  {
    id: 'b27',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Server migration',
    startTime: getFullDayStart(6),
    endTime: getFullDayEnd(6),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c6',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Migrate to new cloud infrastructure',
  },
  // Half-day booking with Project only
  {
    id: 'b28',
    resourceId: '11',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data validation',
    startTime: getDateTimeOffset(6, 9, 0),
    endTime: getDateTimeOffset(6, 13, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Validate migrated data integrity',
  },
  // Hourly booking with Project + Cost Centre
  {
    id: 'b29',
    resourceId: '12',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint retrospective',
    startTime: getDateTimeOffset(6, 15, 0),
    endTime: getDateTimeOffset(6, 17, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    clientId: 'c7',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Team retrospective meeting',
  },

  // ===== WEEK 2 (Days 7-13) =====
  // Full-day booking
  {
    id: 'b30',
    resourceId: '7',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Backend optimization',
    startTime: getFullDayStart(10),
    endTime: getFullDayEnd(10),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    clientId: 'c1',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Database query optimization',
  },
  // Multi-day full-day booking
  {
    id: 'b31',
    resourceId: '8',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API v2 development',
    startTime: getFullDayStart(11),
    endTime: getMultiDayEnd(11, 2),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'New API version development',
  },
  // Half-day booking
  {
    id: 'b32',
    resourceId: '9',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Dashboard widgets',
    startTime: getDateTimeOffset(12, 9, 0),
    endTime: getDateTimeOffset(12, 13, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build analytics dashboard widgets',
  },
  // Hourly booking
  {
    id: 'b33',
    resourceId: '13',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Backlog grooming',
    startTime: getDateTimeOffset(13, 14, 0),
    endTime: getDateTimeOffset(13, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Prioritize backlog items',
  },

  // ===== WEEK 3 (Days 14-20) =====
  // Full-day booking
  {
    id: 'b34',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App launch prep',
    startTime: getFullDayStart(14),
    endTime: getFullDayEnd(14),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Final preparations for app launch',
  },
  // Multi-day full-day booking
  {
    id: 'b35',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Cloud migration',
    startTime: getFullDayStart(15),
    endTime: getMultiDayEnd(15, 3),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'AWS to Azure migration',
  },
  // Half-day booking
  {
    id: 'b36',
    resourceId: '3',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Integration testing',
    startTime: getDateTimeOffset(16, 13, 0),
    endTime: getDateTimeOffset(16, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'End-to-end integration tests',
  },
  // Hourly booking
  {
    id: 'b37',
    resourceId: '15',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data cleanup',
    startTime: getDateTimeOffset(17, 10, 0),
    endTime: getDateTimeOffset(17, 12, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Clean up legacy data',
  },
  // Full-day booking
  {
    id: 'b38',
    resourceId: '2',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal redesign',
    startTime: getFullDayStart(18),
    endTime: getFullDayEnd(18),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'New portal design implementation',
  },
  // Half-day booking
  {
    id: 'b39',
    resourceId: '4',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Code review',
    startTime: getDateTimeOffset(19, 9, 0),
    endTime: getDateTimeOffset(19, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Review pull requests',
  },

  // ===== WEEK 4 (Days 21-27) =====
  // Full-day booking
  {
    id: 'b40',
    resourceId: '5',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API documentation',
    startTime: getFullDayStart(21),
    endTime: getFullDayEnd(21),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Update API documentation',
  },
  // Multi-day full-day booking
  {
    id: 'b41',
    resourceId: '6',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App testing',
    startTime: getFullDayStart(22),
    endTime: getMultiDayEnd(22, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'User acceptance testing',
  },
  // Half-day booking
  {
    id: 'b42',
    resourceId: '10',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Security audit',
    startTime: getDateTimeOffset(23, 9, 0),
    endTime: getDateTimeOffset(23, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Infrastructure security review',
  },
  // Hourly booking
  {
    id: 'b43',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint demo',
    startTime: getDateTimeOffset(24, 14, 0),
    endTime: getDateTimeOffset(24, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint demo to stakeholders',
  },
  // Full-day booking
  {
    id: 'b44',
    resourceId: '12',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Regression testing',
    startTime: getFullDayStart(25),
    endTime: getFullDayEnd(25),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Full regression test suite',
  },
  // Half-day booking
  {
    id: 'b45',
    resourceId: '13',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Feature demo',
    startTime: getDateTimeOffset(26, 13, 0),
    endTime: getDateTimeOffset(26, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Demo new features to client',
  },
  // Hourly booking
  {
    id: 'b46',
    resourceId: '7',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data verification',
    startTime: getDateTimeOffset(27, 10, 0),
    endTime: getDateTimeOffset(27, 12, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Verify data migration accuracy',
  },

  // ===== WEEK 5 (Days 28-34) =====
  // Full-day booking
  {
    id: 'b47',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Performance optimization',
    startTime: getFullDayStart(28),
    endTime: getFullDayEnd(28),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Website performance tuning',
  },
  // Half-day booking
  {
    id: 'b48',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'App store submission',
    startTime: getDateTimeOffset(29, 9, 0),
    endTime: getDateTimeOffset(29, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Prepare app store submission',
  },
  // Hourly booking
  {
    id: 'b49',
    resourceId: '3',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API versioning',
    startTime: getDateTimeOffset(30, 14, 0),
    endTime: getDateTimeOffset(30, 16, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement API versioning strategy',
  },
  // Multi-day full-day booking
  {
    id: 'b50',
    resourceId: '8',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Disaster recovery setup',
    startTime: getFullDayStart(31),
    endTime: getMultiDayEnd(31, 2),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup disaster recovery procedures',
  },
  // Half-day booking
  {
    id: 'b51',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'UAT preparation',
    startTime: getDateTimeOffset(33, 13, 0),
    endTime: getDateTimeOffset(33, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Prepare UAT environment',
  },

  // ===== WEEK 6 (Days 35-41) =====
  // Full-day booking
  {
    id: 'b52',
    resourceId: '4',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal launch',
    startTime: getFullDayStart(35),
    endTime: getFullDayEnd(35),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Client portal go-live',
  },
  // Hourly booking
  {
    id: 'b53',
    resourceId: '5',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data archival',
    startTime: getDateTimeOffset(36, 10, 0),
    endTime: getDateTimeOffset(36, 12, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Archive legacy data',
  },
  // Half-day booking
  {
    id: 'b54',
    resourceId: '10',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Q2 planning',
    startTime: getDateTimeOffset(37, 9, 0),
    endTime: getDateTimeOffset(37, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Q2 roadmap planning session',
  },
  // Full-day booking
  {
    id: 'b55',
    resourceId: '11',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'SEO optimization',
    startTime: getFullDayStart(38),
    endTime: getFullDayEnd(38),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'SEO improvements and meta tags',
  },
  // Hourly booking
  {
    id: 'b56',
    resourceId: '12',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Push notifications',
    startTime: getDateTimeOffset(39, 14, 0),
    endTime: getDateTimeOffset(39, 17, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Implement push notification system',
  },

  // ===== WEEK 7 (Days 42-48) =====
  // Multi-day full-day booking
  {
    id: 'b57',
    resourceId: '6',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API gateway setup',
    startTime: getFullDayStart(42),
    endTime: getMultiDayEnd(42, 3),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Configure API gateway and rate limiting',
  },
  // Half-day booking
  {
    id: 'b58',
    resourceId: '13',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Monitoring setup',
    startTime: getDateTimeOffset(45, 9, 0),
    endTime: getDateTimeOffset(45, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup Grafana dashboards',
  },
  // Hourly booking
  {
    id: 'b59',
    resourceId: '14',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test report',
    startTime: getDateTimeOffset(46, 14, 0),
    endTime: getDateTimeOffset(46, 16, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Generate test coverage report',
  },
  // Full-day booking
  {
    id: 'b60',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'User training',
    startTime: getFullDayStart(47),
    endTime: getFullDayEnd(47),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'End user training session',
  },

  // ===== WEEK 8 (Days 49-55) =====
  // Full-day booking
  {
    id: 'b61',
    resourceId: '1',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Final migration',
    startTime: getFullDayStart(49),
    endTime: getFullDayEnd(49),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Final data migration cutover',
  },
  // Half-day booking
  {
    id: 'b62',
    resourceId: '2',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint review',
    startTime: getDateTimeOffset(50, 13, 0),
    endTime: getDateTimeOffset(50, 17, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'End of sprint review meeting',
  },
  // Hourly booking
  {
    id: 'b63',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Analytics review',
    startTime: getDateTimeOffset(51, 10, 0),
    endTime: getDateTimeOffset(51, 12, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Review website analytics',
  },
  // Multi-day full-day booking
  {
    id: 'b64',
    resourceId: '7',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App v2 planning',
    startTime: getFullDayStart(52),
    endTime: getMultiDayEnd(52, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Planning for app version 2',
  },
  // Half-day booking
  {
    id: 'b65',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API monitoring',
    startTime: getDateTimeOffset(54, 9, 0),
    endTime: getDateTimeOffset(54, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Setup API monitoring alerts',
  },

  // ===== PREVIOUS WEEK 1 (Days -7 to -1) =====
  // Full-day booking
  {
    id: 'b66',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Initial design review',
    startTime: getFullDayStart(-7),
    endTime: getFullDayEnd(-7),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Review initial design mockups',
  },
  // Half-day booking
  {
    id: 'b67',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Wireframe review',
    startTime: getDateTimeOffset(-6, 9, 0),
    endTime: getDateTimeOffset(-6, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Review app wireframes',
  },
  // Hourly booking
  {
    id: 'b68',
    resourceId: '3',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API planning',
    startTime: getDateTimeOffset(-5, 14, 0),
    endTime: getDateTimeOffset(-5, 16, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Plan API architecture',
  },
  // Full-day booking
  {
    id: 'b69',
    resourceId: '4',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Server setup',
    startTime: getFullDayStart(-4),
    endTime: getFullDayEnd(-4),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Initial server configuration',
  },
  // Half-day booking
  {
    id: 'b70',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test planning',
    startTime: getDateTimeOffset(-3, 13, 0),
    endTime: getDateTimeOffset(-3, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Create test plan document',
  },
  // Hourly booking
  {
    id: 'b71',
    resourceId: '6',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Requirements gathering',
    startTime: getDateTimeOffset(-2, 10, 0),
    endTime: getDateTimeOffset(-2, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Gather portal requirements',
  },

  // ===== PREVIOUS WEEK 2 (Days -14 to -8) =====
  // Multi-day full-day booking
  {
    id: 'b72',
    resourceId: '7',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data assessment',
    startTime: getFullDayStart(-14),
    endTime: getMultiDayEnd(-14, 2),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Assess data migration scope',
  },
  // Half-day booking
  {
    id: 'b73',
    resourceId: '8',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint kickoff',
    startTime: getDateTimeOffset(-12, 9, 0),
    endTime: getDateTimeOffset(-12, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint kickoff meeting',
  },
  // Hourly booking
  {
    id: 'b74',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Stakeholder meeting',
    startTime: getDateTimeOffset(-11, 14, 0),
    endTime: getDateTimeOffset(-11, 16, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Meet with stakeholders',
  },
  // Full-day booking
  {
    id: 'b75',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'UI prototyping',
    startTime: getFullDayStart(-10),
    endTime: getFullDayEnd(-10),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Create UI prototypes',
  },
  // Half-day booking
  {
    id: 'b76',
    resourceId: '11',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API design',
    startTime: getDateTimeOffset(-9, 13, 0),
    endTime: getDateTimeOffset(-9, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Design API endpoints',
  },

  // ===== PREVIOUS WEEK 3 (Days -21 to -15) =====
  // Full-day booking
  {
    id: 'b77',
    resourceId: '12',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Cloud planning',
    startTime: getFullDayStart(-21),
    endTime: getFullDayEnd(-21),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Plan cloud architecture',
  },
  // Hourly booking
  {
    id: 'b78',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Test strategy',
    startTime: getDateTimeOffset(-20, 10, 0),
    endTime: getDateTimeOffset(-20, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Define test strategy',
  },
  // Half-day booking
  {
    id: 'b79',
    resourceId: '14',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal planning',
    startTime: getDateTimeOffset(-19, 9, 0),
    endTime: getDateTimeOffset(-19, 13, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Plan portal features',
  },
  // Multi-day full-day booking
  {
    id: 'b80',
    resourceId: '15',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data mapping',
    startTime: getFullDayStart(-18),
    endTime: getMultiDayEnd(-18, 2),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Map source to target data',
  },
  // Hourly booking
  {
    id: 'b81',
    resourceId: '1',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Backlog review',
    startTime: getDateTimeOffset(-16, 14, 0),
    endTime: getDateTimeOffset(-16, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Review product backlog',
  },

  // ===== PREVIOUS WEEK 4 (Days -28 to -22) =====
  // Full-day booking
  {
    id: 'b82',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Project kickoff',
    startTime: getFullDayStart(-28),
    endTime: getFullDayEnd(-28),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Website redesign project kickoff',
  },
  // Half-day booking
  {
    id: 'b83',
    resourceId: '3',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Discovery session',
    startTime: getDateTimeOffset(-27, 9, 0),
    endTime: getDateTimeOffset(-27, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'App discovery workshop',
  },
  // Hourly booking
  {
    id: 'b84',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Tech assessment',
    startTime: getDateTimeOffset(-26, 14, 0),
    endTime: getDateTimeOffset(-26, 16, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Technical assessment meeting',
  },
  // Full-day booking
  {
    id: 'b85',
    resourceId: '5',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Requirements gathering',
    startTime: getFullDayStart(-25),
    endTime: getFullDayEnd(-25),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Gather infrastructure requirements',
  },
  // Half-day booking
  {
    id: 'b86',
    resourceId: '6',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'QA kickoff',
    startTime: getDateTimeOffset(-24, 13, 0),
    endTime: getDateTimeOffset(-24, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'QA team kickoff meeting',
  },
  // Hourly booking
  {
    id: 'b87',
    resourceId: '7',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Scope definition',
    startTime: getDateTimeOffset(-23, 10, 0),
    endTime: getDateTimeOffset(-23, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Define project scope',
  },
  // Multi-day full-day booking
  {
    id: 'b88',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data discovery',
    startTime: getFullDayStart(-22),
    endTime: getMultiDayEnd(-22, 2),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Discover and catalog data sources',
  },

  // ===== ADDITIONAL BOOKINGS FOR LAST 4 WEEKS =====
  // Day -1 (yesterday)
  {
    id: 'b89',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Code deployment',
    startTime: getFullDayStart(-1),
    endTime: getFullDayEnd(-1),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Deploy latest changes to production',
  },
  // Day -8
  {
    id: 'b90',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App beta testing',
    startTime: getDateTimeOffset(-8, 9, 0),
    endTime: getDateTimeOffset(-8, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Internal beta testing session',
  },
  // Day -13
  {
    id: 'b91',
    resourceId: '11',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'API load testing',
    startTime: getDateTimeOffset(-13, 14, 0),
    endTime: getDateTimeOffset(-13, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Perform API load testing',
  },
  // Day -15
  {
    id: 'b92',
    resourceId: '12',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Network configuration',
    startTime: getFullDayStart(-15),
    endTime: getFullDayEnd(-15),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure network settings',
  },
  // Day -17
  {
    id: 'b93',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Bug triage',
    startTime: getDateTimeOffset(-17, 9, 0),
    endTime: getDateTimeOffset(-17, 13, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Triage and prioritize bugs',
  },
  // Day -19
  {
    id: 'b94',
    resourceId: '14',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'User feedback review',
    startTime: getDateTimeOffset(-19, 14, 0),
    endTime: getDateTimeOffset(-19, 16, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Review user feedback',
  },
  // Day -21
  {
    id: 'b95',
    resourceId: '15',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data validation scripts',
    startTime: getFullDayStart(-21),
    endTime: getFullDayEnd(-21),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Write data validation scripts',
  },
  // Day -24
  {
    id: 'b96',
    resourceId: '9',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Team standup',
    startTime: getDateTimeOffset(-24, 9, 0),
    endTime: getDateTimeOffset(-24, 10, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Daily standup meeting',
  },
  // Day -26
  {
    id: 'b97',
    resourceId: '10',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Design review',
    startTime: getDateTimeOffset(-26, 13, 0),
    endTime: getDateTimeOffset(-26, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Review design mockups',
  },
  // Day -28
  {
    id: 'b98',
    resourceId: '11',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App architecture',
    startTime: getFullDayStart(-28),
    endTime: getFullDayEnd(-28),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Define app architecture',
  },

  // ===== ADDITIONAL BOOKINGS FOR NEXT 4 WEEKS =====
  // Day 28
  {
    id: 'b99',
    resourceId: '12',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API caching',
    startTime: getDateTimeOffset(28, 9, 0),
    endTime: getDateTimeOffset(28, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement API caching layer',
  },
  // Day 30
  {
    id: 'b100',
    resourceId: '13',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'SSL certificates',
    startTime: getDateTimeOffset(30, 10, 0),
    endTime: getDateTimeOffset(30, 12, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Renew SSL certificates',
  },
  // Day 32
  {
    id: 'b101',
    resourceId: '14',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Smoke testing',
    startTime: getFullDayStart(32),
    endTime: getFullDayEnd(32),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Smoke testing after deployment',
  },
  // Day 35
  {
    id: 'b102',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal analytics',
    startTime: getDateTimeOffset(35, 13, 0),
    endTime: getDateTimeOffset(35, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Setup portal analytics',
  },
  // Day 38
  {
    id: 'b103',
    resourceId: '9',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data backup',
    startTime: getDateTimeOffset(38, 9, 0),
    endTime: getDateTimeOffset(38, 11, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Backup data before migration',
  },
  // Day 40
  {
    id: 'b104',
    resourceId: '10',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint retrospective',
    startTime: getFullDayStart(40),
    endTime: getFullDayEnd(40),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'End of sprint retrospective',
  },
  // Day 43
  {
    id: 'b105',
    resourceId: '11',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'A/B testing',
    startTime: getDateTimeOffset(43, 9, 0),
    endTime: getDateTimeOffset(43, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Setup A/B testing framework',
  },
  // Day 46
  {
    id: 'b106',
    resourceId: '12',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'App analytics',
    startTime: getDateTimeOffset(46, 14, 0),
    endTime: getDateTimeOffset(46, 16, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Implement app analytics',
  },
  // Day 50
  {
    id: 'b107',
    resourceId: '13',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API documentation update',
    startTime: getFullDayStart(50),
    endTime: getFullDayEnd(50),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Update API documentation',
  },
  // Day 53
  {
    id: 'b108',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Load balancer config',
    startTime: getDateTimeOffset(53, 9, 0),
    endTime: getDateTimeOffset(53, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure load balancer',
  },

  // ===== ADDITIONAL BOOKINGS FOR CURRENT WEEK (Days 0-6) =====
  // Day 0 (today) - additional
  {
    id: 'b109',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Unit test review',
    startTime: getDateTimeOffset(-2, 14, 0),
    endTime: getDateTimeOffset(-2, 16, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Review unit test coverage',
  },
  // Day 1
  {
    id: 'b110',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal security audit',
    startTime: getFullDayStart(1),
    endTime: getFullDayEnd(1),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Security audit for portal',
  },
  // Day 2
  {
    id: 'b111',
    resourceId: '11',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'ETL pipeline',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 13, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Build ETL pipeline',
  },
  // Day 3
  {
    id: 'b112',
    resourceId: '12',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Story pointing',
    startTime: getDateTimeOffset(3, 14, 0),
    endTime: getDateTimeOffset(3, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Story pointing session',
  },
  // Day 4
  {
    id: 'b113',
    resourceId: '13',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Content migration',
    startTime: getFullDayStart(4),
    endTime: getFullDayEnd(4),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Migrate content to new CMS',
  },
  // Day 5
  {
    id: 'b114',
    resourceId: '14',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'App localization',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Implement app localization',
  },
  // Day 6
  {
    id: 'b115',
    resourceId: '15',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API rate limiting',
    startTime: getDateTimeOffset(6, 14, 0),
    endTime: getDateTimeOffset(6, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement API rate limiting',
  },

  // ===== 10 MORE BOOKINGS FOR PREVIOUS WEEK 4 (Days -28 to -22) =====
  {
    id: 'b116',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Initial wireframes',
    startTime: getFullDayStart(-28),
    endTime: getFullDayEnd(-28),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Create initial wireframes',
  },
  {
    id: 'b117',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'User research',
    startTime: getDateTimeOffset(-27, 9, 0),
    endTime: getDateTimeOffset(-27, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Conduct user research',
  },
  {
    id: 'b118',
    resourceId: '3',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API spec review',
    startTime: getDateTimeOffset(-26, 14, 0),
    endTime: getDateTimeOffset(-26, 16, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Review API specifications',
  },
  {
    id: 'b119',
    resourceId: '4',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Server provisioning',
    startTime: getFullDayStart(-25),
    endTime: getFullDayEnd(-25),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Provision new servers',
  },
  {
    id: 'b120',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Test env setup',
    startTime: getDateTimeOffset(-24, 9, 0),
    endTime: getDateTimeOffset(-24, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Setup test environment',
  },
  {
    id: 'b121',
    resourceId: '6',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal mockups',
    startTime: getDateTimeOffset(-23, 13, 0),
    endTime: getDateTimeOffset(-23, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Create portal mockups',
  },
  {
    id: 'b122',
    resourceId: '7',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Schema analysis',
    startTime: getFullDayStart(-22),
    endTime: getFullDayEnd(-22),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Analyze database schema',
  },
  {
    id: 'b123',
    resourceId: '8',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Capacity planning',
    startTime: getDateTimeOffset(-28, 14, 0),
    endTime: getDateTimeOffset(-28, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Team capacity planning',
  },
  {
    id: 'b124',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Brand guidelines',
    startTime: getDateTimeOffset(-27, 13, 0),
    endTime: getDateTimeOffset(-27, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Review brand guidelines',
  },
  {
    id: 'b125',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Competitor analysis',
    startTime: getFullDayStart(-26),
    endTime: getFullDayEnd(-26),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Analyze competitor apps',
  },

  // ===== 10 MORE BOOKINGS FOR PREVIOUS WEEK 3 (Days -21 to -15) =====
  {
    id: 'b126',
    resourceId: '11',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Auth implementation',
    startTime: getFullDayStart(-21),
    endTime: getFullDayEnd(-21),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement OAuth2 authentication',
  },
  {
    id: 'b127',
    resourceId: '12',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Docker setup',
    startTime: getDateTimeOffset(-20, 9, 0),
    endTime: getDateTimeOffset(-20, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup Docker containers',
  },
  {
    id: 'b128',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test data prep',
    startTime: getDateTimeOffset(-19, 14, 0),
    endTime: getDateTimeOffset(-19, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Prepare test data',
  },
  {
    id: 'b129',
    resourceId: '14',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Auth flow design',
    startTime: getFullDayStart(-18),
    endTime: getFullDayEnd(-18),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design authentication flow',
  },
  {
    id: 'b130',
    resourceId: '15',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'ETL design',
    startTime: getDateTimeOffset(-17, 9, 0),
    endTime: getDateTimeOffset(-17, 13, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Design ETL processes',
  },
  {
    id: 'b131',
    resourceId: '1',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Risk assessment',
    startTime: getDateTimeOffset(-16, 14, 0),
    endTime: getDateTimeOffset(-16, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint risk assessment',
  },
  {
    id: 'b132',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Color palette',
    startTime: getFullDayStart(-15),
    endTime: getFullDayEnd(-15),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Define color palette',
  },
  {
    id: 'b133',
    resourceId: '3',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Icon design',
    startTime: getDateTimeOffset(-21, 9, 0),
    endTime: getDateTimeOffset(-21, 12, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design app icons',
  },
  {
    id: 'b134',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Error handling',
    startTime: getDateTimeOffset(-20, 13, 0),
    endTime: getDateTimeOffset(-20, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement error handling',
  },
  {
    id: 'b135',
    resourceId: '5',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'CI/CD pipeline',
    startTime: getFullDayStart(-19),
    endTime: getFullDayEnd(-19),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup CI/CD pipeline',
  },

  // ===== 10 MORE BOOKINGS FOR PREVIOUS WEEK 2 (Days -14 to -8) =====
  {
    id: 'b136',
    resourceId: '6',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'API testing',
    startTime: getFullDayStart(-14),
    endTime: getFullDayEnd(-14),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'API endpoint testing',
  },
  {
    id: 'b137',
    resourceId: '7',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Dashboard design',
    startTime: getDateTimeOffset(-13, 9, 0),
    endTime: getDateTimeOffset(-13, 13, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design dashboard layout',
  },
  {
    id: 'b138',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data cleansing',
    startTime: getDateTimeOffset(-12, 14, 0),
    endTime: getDateTimeOffset(-12, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Cleanse source data',
  },
  {
    id: 'b139',
    resourceId: '9',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Velocity review',
    startTime: getFullDayStart(-11),
    endTime: getFullDayEnd(-11),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Review team velocity',
  },
  {
    id: 'b140',
    resourceId: '10',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Typography',
    startTime: getDateTimeOffset(-10, 9, 0),
    endTime: getDateTimeOffset(-10, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Define typography system',
  },
  {
    id: 'b141',
    resourceId: '11',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Navigation design',
    startTime: getDateTimeOffset(-9, 14, 0),
    endTime: getDateTimeOffset(-9, 16, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design app navigation',
  },
  {
    id: 'b142',
    resourceId: '12',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Rate limiting',
    startTime: getFullDayStart(-8),
    endTime: getFullDayEnd(-8),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Implement rate limiting',
  },
  {
    id: 'b143',
    resourceId: '13',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Logging setup',
    startTime: getDateTimeOffset(-14, 9, 0),
    endTime: getDateTimeOffset(-14, 12, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup centralized logging',
  },
  {
    id: 'b144',
    resourceId: '14',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'E2E testing',
    startTime: getDateTimeOffset(-13, 13, 0),
    endTime: getDateTimeOffset(-13, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'End-to-end testing',
  },
  {
    id: 'b145',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Reports module',
    startTime: getFullDayStart(-12),
    endTime: getFullDayEnd(-12),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build reports module',
  },

  // ===== 10 MORE BOOKINGS FOR PREVIOUS WEEK 1 (Days -7 to -1) =====
  {
    id: 'b146',
    resourceId: '1',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Migration scripts',
    startTime: getFullDayStart(-7),
    endTime: getFullDayEnd(-7),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Write migration scripts',
  },
  {
    id: 'b147',
    resourceId: '2',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint review',
    startTime: getDateTimeOffset(-6, 9, 0),
    endTime: getDateTimeOffset(-6, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint review meeting',
  },
  {
    id: 'b148',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Component library',
    startTime: getDateTimeOffset(-5, 14, 0),
    endTime: getDateTimeOffset(-5, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Build component library',
  },
  {
    id: 'b149',
    resourceId: '4',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Animation design',
    startTime: getFullDayStart(-4),
    endTime: getFullDayEnd(-4),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design app animations',
  },
  {
    id: 'b150',
    resourceId: '5',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Webhook setup',
    startTime: getDateTimeOffset(-3, 9, 0),
    endTime: getDateTimeOffset(-3, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Setup webhook endpoints',
  },
  {
    id: 'b151',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Backup config',
    startTime: getDateTimeOffset(-2, 14, 0),
    endTime: getDateTimeOffset(-2, 16, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure backup system',
  },
  {
    id: 'b152',
    resourceId: '7',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Performance test',
    startTime: getFullDayStart(-1),
    endTime: getFullDayEnd(-1),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Performance testing',
  },
  {
    id: 'b153',
    resourceId: '8',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'User settings',
    startTime: getDateTimeOffset(-7, 9, 0),
    endTime: getDateTimeOffset(-7, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build user settings page',
  },
  {
    id: 'b154',
    resourceId: '9',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Rollback plan',
    startTime: getDateTimeOffset(-6, 13, 0),
    endTime: getDateTimeOffset(-6, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Create rollback plan',
  },
  {
    id: 'b155',
    resourceId: '10',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Retro meeting',
    startTime: getFullDayStart(-5),
    endTime: getFullDayEnd(-5),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint retrospective',
  },

  // ===== 10 MORE BOOKINGS FOR WEEK 5 (Days 28-34) =====
  {
    id: 'b156',
    resourceId: '11',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Launch prep',
    startTime: getFullDayStart(28),
    endTime: getFullDayEnd(28),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Prepare for launch',
  },
  {
    id: 'b157',
    resourceId: '12',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Beta feedback',
    startTime: getDateTimeOffset(29, 9, 0),
    endTime: getDateTimeOffset(29, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Review beta feedback',
  },
  {
    id: 'b158',
    resourceId: '13',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API metrics',
    startTime: getDateTimeOffset(30, 14, 0),
    endTime: getDateTimeOffset(30, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Setup API metrics',
  },
  {
    id: 'b159',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Auto-scaling',
    startTime: getFullDayStart(31),
    endTime: getFullDayEnd(31),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure auto-scaling',
  },
  {
    id: 'b160',
    resourceId: '15',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Security testing',
    startTime: getDateTimeOffset(32, 9, 0),
    endTime: getDateTimeOffset(32, 13, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Security penetration testing',
  },
  {
    id: 'b161',
    resourceId: '1',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Notifications',
    startTime: getDateTimeOffset(33, 14, 0),
    endTime: getDateTimeOffset(33, 16, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build notification system',
  },
  {
    id: 'b162',
    resourceId: '2',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data sync',
    startTime: getFullDayStart(34),
    endTime: getFullDayEnd(34),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Setup data sync',
  },
  {
    id: 'b163',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'OKR review',
    startTime: getDateTimeOffset(28, 9, 0),
    endTime: getDateTimeOffset(28, 12, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Review OKRs',
  },
  {
    id: 'b164',
    resourceId: '4',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'SEO audit',
    startTime: getDateTimeOffset(29, 13, 0),
    endTime: getDateTimeOffset(29, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Conduct SEO audit',
  },
  {
    id: 'b165',
    resourceId: '5',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App store assets',
    startTime: getFullDayStart(30),
    endTime: getFullDayEnd(30),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Create app store assets',
  },

  // ===== 10 MORE BOOKINGS FOR WEEK 6 (Days 35-41) =====
  {
    id: 'b166',
    resourceId: '6',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API docs',
    startTime: getFullDayStart(35),
    endTime: getFullDayEnd(35),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Write API documentation',
  },
  {
    id: 'b167',
    resourceId: '7',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'CDN setup',
    startTime: getDateTimeOffset(36, 9, 0),
    endTime: getDateTimeOffset(36, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup CDN',
  },
  {
    id: 'b168',
    resourceId: '8',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Load testing',
    startTime: getDateTimeOffset(37, 14, 0),
    endTime: getDateTimeOffset(37, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Load testing',
  },
  {
    id: 'b169',
    resourceId: '9',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Search feature',
    startTime: getFullDayStart(38),
    endTime: getFullDayEnd(38),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build search feature',
  },
  {
    id: 'b170',
    resourceId: '10',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data validation',
    startTime: getDateTimeOffset(39, 9, 0),
    endTime: getDateTimeOffset(39, 13, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Validate migrated data',
  },
  {
    id: 'b171',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Roadmap update',
    startTime: getDateTimeOffset(40, 14, 0),
    endTime: getDateTimeOffset(40, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Update product roadmap',
  },
  {
    id: 'b172',
    resourceId: '12',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Accessibility',
    startTime: getFullDayStart(41),
    endTime: getFullDayEnd(41),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Accessibility improvements',
  },
  {
    id: 'b173',
    resourceId: '13',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Dark mode',
    startTime: getDateTimeOffset(35, 9, 0),
    endTime: getDateTimeOffset(35, 12, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Implement dark mode',
  },
  {
    id: 'b174',
    resourceId: '14',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Batch endpoints',
    startTime: getDateTimeOffset(36, 13, 0),
    endTime: getDateTimeOffset(36, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Build batch endpoints',
  },
  {
    id: 'b175',
    resourceId: '15',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'DNS config',
    startTime: getFullDayStart(37),
    endTime: getFullDayEnd(37),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure DNS',
  },

  // ===== 10 MORE BOOKINGS FOR WEEK 7 (Days 42-48) =====
  {
    id: 'b176',
    resourceId: '1',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Regression suite',
    startTime: getFullDayStart(42),
    endTime: getFullDayEnd(42),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Run regression suite',
  },
  {
    id: 'b177',
    resourceId: '2',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Export feature',
    startTime: getDateTimeOffset(43, 9, 0),
    endTime: getDateTimeOffset(43, 13, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Build export feature',
  },
  {
    id: 'b178',
    resourceId: '3',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Cutover plan',
    startTime: getDateTimeOffset(44, 14, 0),
    endTime: getDateTimeOffset(44, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Plan cutover',
  },
  {
    id: 'b179',
    resourceId: '4',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Team sync',
    startTime: getFullDayStart(45),
    endTime: getFullDayEnd(45),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Team sync meeting',
  },
  {
    id: 'b180',
    resourceId: '5',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Final review',
    startTime: getDateTimeOffset(46, 9, 0),
    endTime: getDateTimeOffset(46, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Final design review',
  },
  {
    id: 'b181',
    resourceId: '6',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App polish',
    startTime: getDateTimeOffset(47, 14, 0),
    endTime: getDateTimeOffset(47, 16, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Polish app UI',
  },
  {
    id: 'b182',
    resourceId: '7',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API testing',
    startTime: getFullDayStart(48),
    endTime: getFullDayEnd(48),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'API integration testing',
  },
  {
    id: 'b183',
    resourceId: '8',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Monitoring',
    startTime: getDateTimeOffset(42, 9, 0),
    endTime: getDateTimeOffset(42, 12, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup monitoring',
  },
  {
    id: 'b184',
    resourceId: '9',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Bug fixes',
    startTime: getDateTimeOffset(43, 13, 0),
    endTime: getDateTimeOffset(43, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Fix critical bugs',
  },
  {
    id: 'b185',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'User testing',
    startTime: getFullDayStart(44),
    endTime: getFullDayEnd(44),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'User acceptance testing',
  },

  // ===== 10 MORE BOOKINGS FOR WEEK 8 (Days 49-55) =====
  {
    id: 'b186',
    resourceId: '11',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Go-live prep',
    startTime: getFullDayStart(49),
    endTime: getFullDayEnd(49),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Prepare for go-live',
  },
  {
    id: 'b187',
    resourceId: '12',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Q3 planning',
    startTime: getDateTimeOffset(50, 9, 0),
    endTime: getDateTimeOffset(50, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Q3 planning session',
  },
  {
    id: 'b188',
    resourceId: '13',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Launch day',
    startTime: getDateTimeOffset(51, 14, 0),
    endTime: getDateTimeOffset(51, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Website launch support',
  },
  {
    id: 'b189',
    resourceId: '14',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App launch',
    startTime: getFullDayStart(52),
    endTime: getFullDayEnd(52),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'App launch day',
  },
  {
    id: 'b190',
    resourceId: '15',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Post-launch',
    startTime: getDateTimeOffset(53, 9, 0),
    endTime: getDateTimeOffset(53, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Post-launch monitoring',
  },
  {
    id: 'b191',
    resourceId: '1',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Optimization',
    startTime: getDateTimeOffset(54, 14, 0),
    endTime: getDateTimeOffset(54, 16, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Performance optimization',
  },
  {
    id: 'b192',
    resourceId: '2',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Final testing',
    startTime: getFullDayStart(55),
    endTime: getFullDayEnd(55),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Final round of testing',
  },
  {
    id: 'b193',
    resourceId: '3',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Training docs',
    startTime: getDateTimeOffset(49, 9, 0),
    endTime: getDateTimeOffset(49, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Write training docs',
  },
  {
    id: 'b194',
    resourceId: '4',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Verification',
    startTime: getDateTimeOffset(50, 13, 0),
    endTime: getDateTimeOffset(50, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Verify migration',
  },
  {
    id: 'b195',
    resourceId: '5',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Lessons learned',
    startTime: getFullDayStart(51),
    endTime: getFullDayEnd(51),
    color: getProjectColor('Sprint Planning'),
    durationType: 'full-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Lessons learned session',
  },

  // ===== UNALLOCATED BOOKINGS (from 3rd party system, no resource assigned) =====
  {
    id: 'u1',
    resourceId: 'unallocated',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Frontend development sprint',
    startTime: getFullDayStart(1),
    endTime: getMultiDayEnd(1, 4),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'Sprint 7 - Responsive navigation overhaul',
    requiredRole: 'Frontend Developer',
    requiredSkills: ['React', 'TypeScript', 'CSS'],
  },
  {
    id: 'u2',
    resourceId: 'unallocated',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Payment gateway integration',
    startTime: getFullDayStart(2),
    endTime: getMultiDayEnd(2, 2),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Stripe payment gateway integration with fraud detection',
    requiredRole: 'Backend Developer',
    requiredSkills: ['Node.js', 'PostgreSQL', 'REST APIs'],
  },
  {
    id: 'u3',
    resourceId: 'unallocated',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Full regression test suite',
    startTime: getFullDayStart(3),
    endTime: getFullDayEnd(3),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Run full regression suite before release candidate',
    requiredRole: 'QA Engineer',
    requiredSkills: ['Test Automation', 'Selenium', 'Jest'],
  },
  {
    id: 'u4',
    resourceId: 'unallocated',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Kubernetes cluster migration',
    startTime: getFullDayStart(5),
    endTime: getMultiDayEnd(5, 3),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Migrate legacy Docker swarm to Kubernetes with zero downtime',
    requiredRole: 'DevOps Engineer',
    requiredSkills: ['Kubernetes', 'Docker', 'AWS'],
  },
  {
    id: 'u5',
    resourceId: 'unallocated',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'UX research & user testing',
    startTime: getFullDayStart(7),
    endTime: getMultiDayEnd(7, 2),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'onsite',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Moderated user testing sessions for redesigned onboarding flow',
    requiredRole: 'UI/UX Designer',
    requiredSkills: ['Figma', 'User Research', 'Prototyping'],
  },
  {
    id: 'u6',
    resourceId: 'unallocated',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    description: 'Q2 roadmap workshop',
    startTime: getDateTimeOffset(8, 9, 0),
    endTime: getDateTimeOffset(8, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Cross-team roadmap alignment for Q2 2025',
    requiredRole: 'Project Manager',
    requiredSkills: ['Agile', 'Scrum', 'Stakeholder Management'],
  },
  {
    id: 'u7',
    resourceId: 'unallocated',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Security penetration testing',
    startTime: getFullDayStart(9),
    endTime: getFullDayEnd(9),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'OWASP Top 10 penetration test on all public API endpoints',
    requiredRole: 'Backend Developer',
    requiredSkills: ['Python', 'Django', 'PostgreSQL'],
  },
  {
    id: 'u8',
    resourceId: 'unallocated',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Analytics & reporting integration',
    startTime: getFullDayStart(10),
    endTime: getMultiDayEnd(10, 1),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'low',
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'New York Office',
    details: 'GA4 migration and custom event tracking implementation',
    requiredRole: 'Frontend Developer',
    requiredSkills: ['JavaScript', 'React', 'CSS'],
  },

  // ===== ADDITIONAL BOOKINGS SPREAD ACROSS RESOURCES =====
  // These bookings are spread across past days to populate the calendar realistically

  // === PAST WEEK DAY -3 - Multiple bookings per resource per day ===
  // Resource 1 - Sarah Johnson - Multiple bookings same day
  {
    id: 'b196',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Morning standup',
    startTime: getDateTimeOffset(-3, 9, 0),
    endTime: getDateTimeOffset(-3, 10, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Daily standup meeting',
  },
  {
    id: 'b197',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Design review',
    startTime: getDateTimeOffset(-3, 11, 0),
    endTime: getDateTimeOffset(-3, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Review mobile app designs',
  },
  {
    id: 'b198',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'API workshop',
    startTime: getDateTimeOffset(-3, 14, 0),
    endTime: getDateTimeOffset(-3, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'API integration workshop',
  },
  // Resource 2 - Michael Chen - Multiple bookings same day
  {
    id: 'b199',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'UI mockups',
    startTime: getDateTimeOffset(-3, 9, 0),
    endTime: getDateTimeOffset(-3, 12, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'San Francisco Office',
    details: 'Create UI mockups',
  },
  {
    id: 'b200',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Prototype review',
    startTime: getDateTimeOffset(-3, 13, 0),
    endTime: getDateTimeOffset(-3, 15, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Review prototype with client',
  },
  {
    id: 'b201',
    resourceId: '2',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Portal design',
    startTime: getDateTimeOffset(-3, 15, 30),
    endTime: getDateTimeOffset(-3, 17, 30),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design portal interface',
  },
  // Resource 3 - Emily Rodriguez - Multiple bookings
  {
    id: 'b202',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint review',
    startTime: getDateTimeOffset(-3, 9, 0),
    endTime: getDateTimeOffset(-3, 11, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint review meeting',
  },
  {
    id: 'b203',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Client call',
    startTime: getDateTimeOffset(-3, 11, 30),
    endTime: getDateTimeOffset(-3, 12, 30),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Client status call',
  },
  {
    id: 'b204',
    resourceId: '3',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Infra planning',
    startTime: getDateTimeOffset(-3, 14, 0),
    endTime: getDateTimeOffset(-3, 16, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Infrastructure planning session',
  },
  // Resource 4 - David Kim - Multiple bookings
  {
    id: 'b205',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API development',
    startTime: getDateTimeOffset(-3, 9, 0),
    endTime: getDateTimeOffset(-3, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'API endpoint development',
  },
  {
    id: 'b206',
    resourceId: '4',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data mapping',
    startTime: getDateTimeOffset(-3, 14, 0),
    endTime: getDateTimeOffset(-3, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Map data fields',
  },
  // Resource 5 - Jessica Williams - Multiple bookings
  {
    id: 'b207',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test execution',
    startTime: getDateTimeOffset(-4, 9, 0),
    endTime: getDateTimeOffset(-4, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Execute test cases',
  },
  {
    id: 'b208',
    resourceId: '5',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'QA review',
    startTime: getDateTimeOffset(-4, 13, 0),
    endTime: getDateTimeOffset(-4, 15, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'QA review session',
  },
  {
    id: 'b209',
    resourceId: '5',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App testing',
    startTime: getDateTimeOffset(-4, 15, 30),
    endTime: getDateTimeOffset(-4, 17, 30),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Mobile app testing',
  },

  // === DAY 1 - Multiple bookings per resource ===
  {
    id: 'b210',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Server setup',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 12, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup production servers',
  },
  {
    id: 'b211',
    resourceId: '6',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Deploy APIs',
    startTime: getDateTimeOffset(1, 13, 0),
    endTime: getDateTimeOffset(1, 16, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Deploy API endpoints',
  },
  {
    id: 'b212',
    resourceId: '7',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Frontend dev',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Frontend development',
  },
  {
    id: 'b213',
    resourceId: '7',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Portal dev',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Portal development',
  },
  {
    id: 'b214',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data analysis',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 11, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Analyze data patterns',
  },
  {
    id: 'b215',
    resourceId: '8',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    description: 'Backlog grooming',
    startTime: getDateTimeOffset(1, 11, 30),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Backlog grooming session',
  },
  {
    id: 'b216',
    resourceId: '8',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test analysis',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Analyze test results',
  },
  // Resource 9-15 Day 1
  {
    id: 'b217',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Code review',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 11, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Code review session',
  },
  {
    id: 'b218',
    resourceId: '9',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App architecture',
    startTime: getDateTimeOffset(1, 11, 30),
    endTime: getDateTimeOffset(1, 13, 30),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Design app architecture',
  },
  {
    id: 'b219',
    resourceId: '9',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'API testing',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Test API endpoints',
  },
  {
    id: 'b220',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'UI design',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design mobile UI',
  },
  {
    id: 'b221',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Portal UX',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design portal UX',
  },
  {
    id: 'b222',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Requirements',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 12, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Gather requirements',
  },
  {
    id: 'b223',
    resourceId: '11',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Stakeholder meeting',
    startTime: getDateTimeOffset(1, 13, 0),
    endTime: getDateTimeOffset(1, 15, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Stakeholder alignment',
  },
  {
    id: 'b224',
    resourceId: '12',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Backend dev',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Backend development',
  },
  {
    id: 'b225',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Regression testing',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Run regression tests',
  },
  {
    id: 'b226',
    resourceId: '13',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App QA',
    startTime: getDateTimeOffset(1, 13, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'QA mobile app',
  },
  {
    id: 'b227',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Cloud config',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure cloud services',
  },
  {
    id: 'b228',
    resourceId: '14',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'ETL setup',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Setup ETL pipelines',
  },
  {
    id: 'b229',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Security audit',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(1, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Security audit',
  },
  {
    id: 'b230',
    resourceId: '15',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    description: 'Retrospective',
    startTime: getDateTimeOffset(1, 14, 0),
    endTime: getDateTimeOffset(1, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint retrospective',
  },

  // === DAY 2 - More overlapping bookings ===
  {
    id: 'b231',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Feature dev',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Feature development',
  },
  {
    id: 'b232',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Wireframes',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 12, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Create wireframes',
  },
  {
    id: 'b233',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Design system',
    startTime: getDateTimeOffset(2, 13, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Build design system',
  },
  {
    id: 'b234',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Planning poker',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 11, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Planning poker session',
  },
  {
    id: 'b235',
    resourceId: '3',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Vendor call',
    startTime: getDateTimeOffset(2, 11, 30),
    endTime: getDateTimeOffset(2, 12, 30),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Vendor coordination call',
  },
  {
    id: 'b236',
    resourceId: '3',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Migration plan',
    startTime: getDateTimeOffset(2, 14, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Create migration plan',
  },
  {
    id: 'b237',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Auth module',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Build auth module',
  },
  {
    id: 'b238',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Performance test',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 13, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Run performance tests',
  },
  {
    id: 'b239',
    resourceId: '5',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'UAT support',
    startTime: getDateTimeOffset(2, 14, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Support UAT testing',
  },
  {
    id: 'b240',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'CI/CD setup',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup CI/CD pipelines',
  },
  {
    id: 'b241',
    resourceId: '7',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Component lib',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 12, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Build component library',
  },
  {
    id: 'b242',
    resourceId: '7',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'React Native',
    startTime: getDateTimeOffset(2, 13, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'React Native development',
  },
  {
    id: 'b243',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Data profiling',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Profile source data',
  },
  {
    id: 'b244',
    resourceId: '9',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Backend APIs',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 13, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Build backend APIs',
  },
  {
    id: 'b245',
    resourceId: '9',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Integration test',
    startTime: getDateTimeOffset(2, 14, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Run integration tests',
  },
  {
    id: 'b246',
    resourceId: '10',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Icon design',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 12, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Design app icons',
  },
  {
    id: 'b247',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Dashboard design',
    startTime: getDateTimeOffset(2, 13, 0),
    endTime: getDateTimeOffset(2, 17, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design dashboard',
  },
  {
    id: 'b248',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Story mapping',
    startTime: getDateTimeOffset(2, 9, 0),
    endTime: getDateTimeOffset(2, 13, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'half-day',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'User story mapping',
  },
  {
    id: 'b249',
    resourceId: '11',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Cost analysis',
    startTime: getDateTimeOffset(2, 14, 0),
    endTime: getDateTimeOffset(2, 16, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Cloud cost analysis',
  },
  {
    id: 'b250',
    resourceId: '12',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Payment API',
    startTime: getFullDayStart(2),
    endTime: getFullDayEnd(2),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Build payment API',
  },

  // === DAY 3-6 - Continue with more bookings ===
  {
    id: 'b251',
    resourceId: '1',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App review',
    startTime: getDateTimeOffset(3, 9, 0),
    endTime: getDateTimeOffset(3, 11, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Review app progress',
  },
  {
    id: 'b252',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Bug fixes',
    startTime: getDateTimeOffset(3, 11, 30),
    endTime: getDateTimeOffset(3, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Fix reported bugs',
  },
  {
    id: 'b253',
    resourceId: '2',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'User flows',
    startTime: getFullDayStart(3),
    endTime: getFullDayEnd(3),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Design user flows',
  },
  {
    id: 'b254',
    resourceId: '3',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Status update',
    startTime: getDateTimeOffset(3, 10, 0),
    endTime: getDateTimeOffset(3, 11, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Weekly status update',
  },
  {
    id: 'b255',
    resourceId: '3',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'Budget review',
    startTime: getDateTimeOffset(3, 14, 0),
    endTime: getDateTimeOffset(3, 16, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Review project budget',
  },
  {
    id: 'b256',
    resourceId: '4',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Script dev',
    startTime: getFullDayStart(3),
    endTime: getFullDayEnd(3),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Develop migration scripts',
  },
  {
    id: 'b257',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Security test',
    startTime: getDateTimeOffset(3, 9, 0),
    endTime: getDateTimeOffset(3, 17, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Security testing',
  },
  {
    id: 'b258',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Monitoring',
    startTime: getDateTimeOffset(3, 9, 0),
    endTime: getDateTimeOffset(3, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Setup monitoring',
  },
  {
    id: 'b259',
    resourceId: '6',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Load testing',
    startTime: getDateTimeOffset(3, 14, 0),
    endTime: getDateTimeOffset(3, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'API load testing',
  },
  {
    id: 'b260',
    resourceId: '7',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Responsive design',
    startTime: getFullDayStart(3),
    endTime: getFullDayEnd(3),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Implement responsive design',
  },
  {
    id: 'b261',
    resourceId: '8',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Metrics review',
    startTime: getDateTimeOffset(3, 9, 0),
    endTime: getDateTimeOffset(3, 11, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Review sprint metrics',
  },
  {
    id: 'b262',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Data validation',
    startTime: getDateTimeOffset(3, 11, 30),
    endTime: getDateTimeOffset(3, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Validate migrated data',
  },

  // === MORE BOOKINGS FOR DAYS 4-6 ===
  {
    id: 'b263',
    resourceId: '9',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Push notifications',
    startTime: getFullDayStart(4),
    endTime: getFullDayEnd(4),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'Implement push notifications',
  },
  {
    id: 'b264',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'Reports design',
    startTime: getDateTimeOffset(4, 9, 0),
    endTime: getDateTimeOffset(4, 13, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'San Francisco Office',
    details: 'Design report templates',
  },
  {
    id: 'b265',
    resourceId: '10',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Animation design',
    startTime: getDateTimeOffset(4, 14, 0),
    endTime: getDateTimeOffset(4, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Design micro-animations',
  },
  {
    id: 'b266',
    resourceId: '11',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API docs',
    startTime: getDateTimeOffset(4, 9, 0),
    endTime: getDateTimeOffset(4, 12, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Write API documentation',
  },
  {
    id: 'b267',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    description: 'Demo prep',
    startTime: getDateTimeOffset(4, 13, 0),
    endTime: getDateTimeOffset(4, 15, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Prepare sprint demo',
  },
  {
    id: 'b268',
    resourceId: '12',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Rollback plan',
    startTime: getDateTimeOffset(4, 9, 0),
    endTime: getDateTimeOffset(4, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Create rollback plan',
  },
  {
    id: 'b269',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'E2E testing',
    startTime: getFullDayStart(4),
    endTime: getFullDayEnd(4),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'End-to-end testing',
  },
  {
    id: 'b270',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Backup config',
    startTime: getDateTimeOffset(4, 9, 0),
    endTime: getDateTimeOffset(4, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Configure backups',
  },
  {
    id: 'b271',
    resourceId: '14',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Webhook setup',
    startTime: getDateTimeOffset(4, 14, 0),
    endTime: getDateTimeOffset(4, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Setup webhooks',
  },
  {
    id: 'b272',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Auth flow',
    startTime: getFullDayStart(4),
    endTime: getFullDayEnd(4),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'Implement auth flow',
  },

  // === DAY 5 BOOKINGS ===
  {
    id: 'b273',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Code freeze',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Code freeze preparation',
  },
  {
    id: 'b274',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'Final review',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'Final design review',
  },
  {
    id: 'b275',
    resourceId: '2',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    description: 'Asset handoff',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Design asset handoff',
  },
  {
    id: 'b276',
    resourceId: '3',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint demo',
    startTime: getDateTimeOffset(5, 10, 0),
    endTime: getDateTimeOffset(5, 12, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint demo presentation',
  },
  {
    id: 'b277',
    resourceId: '3',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Go-live prep',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Go-live preparation',
  },
  {
    id: 'b278',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Final testing',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Final API testing',
  },
  {
    id: 'b279',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    description: 'Sign-off',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Testing sign-off',
  },
  {
    id: 'b280',
    resourceId: '5',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App sign-off',
    startTime: getDateTimeOffset(5, 13, 0),
    endTime: getDateTimeOffset(5, 15, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'App testing sign-off',
  },
  {
    id: 'b281',
    resourceId: '6',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Production deploy',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Production deployment',
  },
  {
    id: 'b282',
    resourceId: '7',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Launch support',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Launch day support',
  },
  {
    id: 'b283',
    resourceId: '8',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Go-live',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Data Migration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Migration go-live',
  },
  {
    id: 'b284',
    resourceId: '9',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    description: 'App store prep',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 13, 0),
    color: getProjectColor('Mobile App Design'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'Remote',
    details: 'App store submission prep',
  },
  {
    id: 'b285',
    resourceId: '9',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'API monitoring',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Setup API monitoring',
  },
  {
    id: 'b286',
    resourceId: '10',
    projectId: 'pd6',
    projectName: 'Client Portal',
    costCentreId: 'cc5',
    description: 'Style guide',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Client Portal'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'San Francisco Office',
    details: 'Create style guide',
  },
  {
    id: 'b287',
    resourceId: '11',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Retro',
    startTime: getDateTimeOffset(5, 15, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Sprint retrospective',
  },
  {
    id: 'b288',
    resourceId: '12',
    projectId: 'pd3',
    projectName: 'API Integration',
    description: 'Handover',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Knowledge handover',
  },
  {
    id: 'b289',
    resourceId: '12',
    projectId: 'pd7',
    projectName: 'Data Migration',
    costCentreId: 'cc5',
    description: 'Post-migration',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Post-migration support',
  },
  {
    id: 'b290',
    resourceId: '13',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Test report',
    startTime: getFullDayStart(5),
    endTime: getFullDayEnd(5),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Create test report',
  },
  {
    id: 'b291',
    resourceId: '14',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    description: 'Runbook',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Create runbook',
  },
  {
    id: 'b292',
    resourceId: '14',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Infra support',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('Website Redesign'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Infrastructure support',
  },
  {
    id: 'b293',
    resourceId: '15',
    projectId: 'pd6',
    projectName: 'Client Portal',
    description: 'UAT support',
    startTime: getDateTimeOffset(5, 9, 0),
    endTime: getDateTimeOffset(5, 12, 0),
    color: getProjectColor('Client Portal'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'RetailMax Inc',
    location: 'Remote',
    details: 'UAT support',
  },
  {
    id: 'b294',
    resourceId: '15',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Next sprint',
    startTime: getDateTimeOffset(5, 14, 0),
    endTime: getDateTimeOffset(5, 16, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'planned',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'Plan next sprint',
  },

  // === DAY 6 BOOKINGS ===
  {
    id: 'b295',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Post-launch',
    startTime: getFullDayStart(6),
    endTime: getFullDayEnd(6),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Post-launch monitoring',
  },
  {
    id: 'b296',
    resourceId: '2',
    projectId: 'pd2',
    projectName: 'Mobile App Design',
    costCentreId: 'cc1',
    description: 'App launch',
    startTime: getFullDayStart(6),
    endTime: getFullDayEnd(6),
    color: getProjectColor('Mobile App Design'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'hybrid',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'HealthTech Solutions',
    location: 'San Francisco Office',
    details: 'App launch day',
  },
  {
    id: 'b297',
    resourceId: '3',
    projectId: 'pd4',
    projectName: 'Infrastructure Setup',
    costCentreId: 'cc2',
    description: 'Monitoring',
    startTime: getDateTimeOffset(6, 9, 0),
    endTime: getDateTimeOffset(6, 13, 0),
    color: getProjectColor('Infrastructure Setup'),
    durationType: 'half-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'GreenEnergy Co',
    location: 'Remote',
    details: 'Production monitoring',
  },
  {
    id: 'b298',
    resourceId: '3',
    projectId: 'pd7',
    projectName: 'Data Migration',
    description: 'Validation',
    startTime: getDateTimeOffset(6, 14, 0),
    endTime: getDateTimeOffset(6, 17, 0),
    color: getProjectColor('Data Migration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'TechStart Inc',
    location: 'Remote',
    details: 'Data validation',
  },
  {
    id: 'b299',
    resourceId: '4',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Bug fixes',
    startTime: getFullDayStart(6),
    endTime: getFullDayEnd(6),
    color: getProjectColor('API Integration'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Fix production bugs',
  },
  {
    id: 'b300',
    resourceId: '5',
    projectId: 'pd5',
    projectName: 'Testing Phase 2',
    costCentreId: 'cc4',
    description: 'Smoke testing',
    startTime: getDateTimeOffset(6, 9, 0),
    endTime: getDateTimeOffset(6, 12, 0),
    color: getProjectColor('Testing Phase 2'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'planned',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'EduLearn Platform',
    location: 'Remote',
    details: 'Production smoke testing',
  },

  // ===== SARAH JOHNSON — TODAY (Day 0) CONCURRENT BOOKINGS =====
  // b1 (above) already covers Website Redesign full-day today.
  // Two additional concurrent bookings show she is double-booked today,
  // providing a realistic overallocation example without being absurd.
  {
    id: 'b302',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc5',
    description: 'Emergency API fixes alongside sprint',
    startTime: getDateTimeOffset(0, 9, 0),
    endTime: getDateTimeOffset(0, 13, 0),
    color: getProjectColor('API Integration'),
    durationType: 'half-day',
    halfDayPeriod: 'am',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Urgent API stability fixes — concurrent with Website Redesign sprint',
  },
  {
    id: 'b303',
    resourceId: '1',
    projectId: 'pd8',
    projectName: 'Sprint Planning',
    costCentreId: 'cc3',
    description: 'Sprint retrospective',
    startTime: getDateTimeOffset(0, 15, 0),
    endTime: getDateTimeOffset(0, 17, 0),
    color: getProjectColor('Sprint Planning'),
    durationType: 'hourly',
    billingClassification: 'internal',
    workType: 'remote',
    status: 'confirmed',
    priority: 'medium',
    notifyReminders: true,
    bookingTypeId: 'bt-4',
    timezone: 'Europe/London',
    customer: 'Internal',
    location: 'Zoom',
    details: 'End-of-sprint retrospective meeting',
  },

  // ===== PROJECT BOOKINGS — ALL 10 DemoProjects =====
  // Matched by projectName so they appear on the Bookings tab in ProjectDetailsModal

  // ── proj-001: UK Digital Transformation (Britvic plc) ──
  {
    id: 'b311', resourceId: '9', projectId: 'proj-001', projectName: 'UK Digital Transformation',
    description: 'Cloud architecture review', startTime: getFullDayStart(-20), endTime: getFullDayEnd(-20),
    color: getProjectColor('UK Digital Transformation'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Britvic plc', location: 'London Office',
    details: 'Phase 1 architecture sign-off session',
  },
  {
    id: 'b312', resourceId: '1', projectId: 'proj-001', projectName: 'UK Digital Transformation',
    description: 'API gateway implementation', startTime: getFullDayStart(-10), endTime: getFullDayEnd(-10),
    color: getProjectColor('UK Digital Transformation'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Britvic plc', location: 'Remote',
    details: 'API gateway build and unit tests',
  },
  {
    id: 'b313', resourceId: '6', projectId: 'proj-001', projectName: 'UK Digital Transformation',
    description: 'CI/CD pipeline setup', startTime: getFullDayStart(5), endTime: getMultiDayEnd(5, 2),
    color: getProjectColor('UK Digital Transformation'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Britvic plc', location: 'Remote',
    details: 'GitHub Actions pipeline and staging environment',
  },
  {
    id: 'b314', resourceId: '1', projectId: 'proj-001', projectName: 'UK Digital Transformation',
    description: 'Phase 2 frontend build', startTime: getFullDayStart(14), endTime: getMultiDayEnd(14, 5),
    color: getProjectColor('UK Digital Transformation'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'hybrid', status: 'confirmed', priority: 'critical', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Britvic plc', location: 'London Office',
    details: 'React dashboard components for Phase 2 delivery',
  },
  {
    id: 'b315', resourceId: 'unallocated', projectId: 'proj-001', projectName: 'UK Digital Transformation',
    description: 'Security penetration testing', startTime: getFullDayStart(21), endTime: getMultiDayEnd(21, 3),
    color: getProjectColor('UK Digital Transformation'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'planned', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Britvic plc', location: 'London Office',
    requiredRole: 'Security Engineer', requiredSkills: ['Penetration Testing', 'OWASP'],
    details: 'Pre-go-live security audit — resource TBC',
  },

  // ── proj-002: USA Enterprise System (Hilton Hotels & Resorts) ──
  {
    id: 'b316', resourceId: '4', projectId: 'proj-002', projectName: 'USA Enterprise System',
    description: 'ERP database schema migration', startTime: getFullDayStart(-15), endTime: getFullDayEnd(-15),
    color: getProjectColor('USA Enterprise System'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/New_York', customer: 'Hilton Hotels & Resorts', location: 'Remote',
    details: 'Finance module schema migration to SQL Server',
  },
  {
    id: 'b317', resourceId: '8', projectId: 'proj-002', projectName: 'USA Enterprise System',
    description: 'Finance reporting UAT', startTime: getDateTimeOffset(-5, 9), endTime: getDateTimeOffset(-5, 17),
    color: getProjectColor('USA Enterprise System'), durationType: 'hourly', billingClassification: 'billable',
    workType: 'onsite', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/New_York', customer: 'Hilton Hotels & Resorts', location: 'New York HQ',
    details: 'UAT session with Hilton finance team',
  },
  {
    id: 'b318', resourceId: '4', projectId: 'proj-002', projectName: 'USA Enterprise System',
    description: 'HR module integration', startTime: getFullDayStart(8), endTime: getMultiDayEnd(8, 3),
    color: getProjectColor('USA Enterprise System'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'America/New_York', customer: 'Hilton Hotels & Resorts', location: 'Remote',
    details: 'HR and payroll module integration work',
  },
  {
    id: 'b319', resourceId: 'unallocated', projectId: 'proj-002', projectName: 'USA Enterprise System',
    description: 'Change management workshops', startTime: getFullDayStart(18), endTime: getMultiDayEnd(18, 2),
    color: getProjectColor('USA Enterprise System'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'planned', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-4', timezone: 'America/New_York', customer: 'Hilton Hotels & Resorts', location: 'New York HQ',
    requiredRole: 'Change Manager', requiredSkills: ['Change Management', 'Training Delivery'],
    details: 'End-user training sessions for finance team',
  },

  // ── proj-003: Germany Industry 4.0 (Siemens AG) ──
  {
    id: 'b320', resourceId: '6', projectId: 'proj-003', projectName: 'Germany Industry 4.0',
    description: 'Infrastructure architecture workshop', startTime: getDateTimeOffset(-12, 9), endTime: getDateTimeOffset(-12, 13),
    color: getProjectColor('Germany Industry 4.0'), durationType: 'hourly', billingClassification: 'billable',
    workType: 'onsite', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Berlin', customer: 'Siemens AG', location: 'Munich Plant',
    details: 'On-site infrastructure scoping with Siemens team',
  },
  {
    id: 'b321', resourceId: '9', projectId: 'proj-003', projectName: 'Germany Industry 4.0',
    description: 'IoT sensor integration', startTime: getFullDayStart(10), endTime: getMultiDayEnd(10, 4),
    color: getProjectColor('Germany Industry 4.0'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'confirmed', priority: 'critical', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Europe/Berlin', customer: 'Siemens AG', location: 'Munich Plant',
    details: 'Plant 1 IoT pilot deployment',
  },
  {
    id: 'b322', resourceId: 'unallocated', projectId: 'proj-003', projectName: 'Germany Industry 4.0',
    description: 'Embedded systems development', startTime: getFullDayStart(25), endTime: getMultiDayEnd(25, 10),
    color: getProjectColor('Germany Industry 4.0'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'planned', priority: 'critical', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Berlin', customer: 'Siemens AG', location: 'Munich Plant',
    requiredRole: 'Embedded Systems Engineer', requiredSkills: ['C++', 'RTOS', 'IoT Protocols'],
    details: 'Phase 1 embedded firmware development — resource required',
  },
  {
    id: 'b323', resourceId: 'unallocated', projectId: 'proj-003', projectName: 'Germany Industry 4.0',
    description: 'Data pipeline & analytics', startTime: getFullDayStart(30), endTime: getMultiDayEnd(30, 5),
    color: getProjectColor('Germany Industry 4.0'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Berlin', customer: 'Siemens AG', location: 'Remote',
    requiredRole: 'Data Scientist', requiredSkills: ['Python', 'Machine Learning', 'Time Series'],
    details: 'Real-time production telemetry analytics',
  },

  // ── proj-004: Australia Mining Tech (BHP Billiton Ltd) ──
  {
    id: 'b324', resourceId: '8', projectId: 'proj-004', projectName: 'Australia Mining Tech',
    description: 'Fleet tracking data analysis', startTime: getFullDayStart(-25), endTime: getFullDayEnd(-25),
    color: getProjectColor('Australia Mining Tech'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Australia/Perth', customer: 'BHP Billiton Ltd', location: 'Remote',
    details: 'Fleet telemetry data modelling and dashboard build',
  },
  {
    id: 'b325', resourceId: '4', projectId: 'proj-004', projectName: 'Australia Mining Tech',
    description: 'Production forecasting API', startTime: getFullDayStart(-8), endTime: getMultiDayEnd(-8, 3),
    color: getProjectColor('Australia Mining Tech'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Australia/Perth', customer: 'BHP Billiton Ltd', location: 'Remote',
    details: 'Predictive analytics endpoints — completed ahead of schedule',
  },
  {
    id: 'b326', resourceId: '7', projectId: 'proj-004', projectName: 'Australia Mining Tech',
    description: 'Mobile app final testing', startTime: getFullDayStart(4), endTime: getMultiDayEnd(4, 2),
    color: getProjectColor('Australia Mining Tech'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Australia/Perth', customer: 'BHP Billiton Ltd', location: 'Remote',
    details: 'iOS and Android final QA pass',
  },

  // ── proj-005: Japan Robotics Initiative (Toyota Motor Corporation) ──
  {
    id: 'b327', resourceId: '9', projectId: 'proj-005', projectName: 'Japan Robotics Initiative',
    description: 'Concept design review', startTime: getDateTimeOffset(-30, 10), endTime: getDateTimeOffset(-30, 14),
    color: getProjectColor('Japan Robotics Initiative'), durationType: 'hourly', billingClassification: 'billable',
    workType: 'onsite', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Asia/Tokyo', customer: 'Toyota Motor Corporation', location: 'Toyota City',
    details: 'Robot arm concept review with Toyota engineering',
  },
  {
    id: 'b328', resourceId: 'unallocated', projectId: 'proj-005', projectName: 'Japan Robotics Initiative',
    description: 'Prototype build — mechanical', startTime: getFullDayStart(12), endTime: getMultiDayEnd(12, 7),
    color: getProjectColor('Japan Robotics Initiative'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'onsite', status: 'planned', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Asia/Tokyo', customer: 'Toyota Motor Corporation', location: 'Toyota City',
    requiredRole: 'Robotics Engineer', requiredSkills: ['Mechanical Design', 'CAD', 'ROS'],
    details: 'Physical prototype assembly — needs Robotics Engineer on-site',
  },

  // ── proj-006: Canada Energy Solutions (EPCOR Utilities Inc.) ──
  {
    id: 'b329', resourceId: '7', projectId: 'proj-006', projectName: 'Canada Energy Solutions',
    description: 'Wind module UI build', startTime: getFullDayStart(-60), endTime: getMultiDayEnd(-60, 5),
    color: getProjectColor('Canada Energy Solutions'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Edmonton', customer: 'EPCOR Utilities Inc.', location: 'Remote',
    details: 'Wind generation dashboard — delivered and signed off',
  },
  {
    id: 'b330', resourceId: '8', projectId: 'proj-006', projectName: 'Canada Energy Solutions',
    description: 'Solar analytics integration', startTime: getFullDayStart(-45), endTime: getMultiDayEnd(-45, 4),
    color: getProjectColor('Canada Energy Solutions'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Edmonton', customer: 'EPCOR Utilities Inc.', location: 'Remote',
    details: 'Solar generation data pipeline and reporting views',
  },
  {
    id: 'b331', resourceId: '1', projectId: 'proj-006', projectName: 'Canada Energy Solutions',
    description: 'Go-live support', startTime: getFullDayStart(-30), endTime: getMultiDayEnd(-30, 2),
    color: getProjectColor('Canada Energy Solutions'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Edmonton', customer: 'EPCOR Utilities Inc.', location: 'Remote',
    details: 'Production go-live support and hypercare',
  },

  // ── proj-007: France Luxury Platform (LVMH Moët Hennessy) ──
  {
    id: 'b332', resourceId: '7', projectId: 'proj-007', projectName: 'France Luxury Platform',
    description: 'Online store v1 frontend', startTime: getFullDayStart(-50), endTime: getMultiDayEnd(-50, 5),
    color: getProjectColor('France Luxury Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Paris', customer: 'LVMH Moët Hennessy', location: 'Remote',
    details: 'Storefront components and checkout flow',
  },
  {
    id: 'b333', resourceId: '4', projectId: 'proj-007', projectName: 'France Luxury Platform',
    description: 'POS integration backend', startTime: getFullDayStart(-20), endTime: getMultiDayEnd(-20, 4),
    color: getProjectColor('France Luxury Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'hybrid', status: 'completed', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Paris', customer: 'LVMH Moët Hennessy', location: 'Paris Office',
    details: 'Boutique POS integration with online platform',
  },
  {
    id: 'b334', resourceId: '2', projectId: 'proj-007', projectName: 'France Luxury Platform',
    description: 'Clienteling module UX', startTime: getFullDayStart(6), endTime: getMultiDayEnd(6, 3),
    color: getProjectColor('France Luxury Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'hybrid', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Europe/Paris', customer: 'LVMH Moët Hennessy', location: 'Paris Office',
    details: 'Clienteling UX design — customer profile and history screens',
  },
  {
    id: 'b335', resourceId: '1', projectId: 'proj-007', projectName: 'France Luxury Platform',
    description: 'Clienteling module build', startTime: getFullDayStart(11), endTime: getMultiDayEnd(11, 5),
    color: getProjectColor('France Luxury Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'Europe/Paris', customer: 'LVMH Moët Hennessy', location: 'Remote',
    details: 'Frontend implementation of clienteling screens',
  },
  {
    id: 'b336', resourceId: 'unallocated', projectId: 'proj-007', projectName: 'France Luxury Platform',
    description: 'International markets feature', startTime: getFullDayStart(35), endTime: getMultiDayEnd(35, 5),
    color: getProjectColor('France Luxury Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/Paris', customer: 'LVMH Moët Hennessy', location: 'Remote',
    requiredRole: 'Backend Developer', requiredSkills: ['Node.js', 'Internationalisation', 'REST APIs'],
    details: 'Multi-currency and localisation support',
  },

  // ── proj-008: UK Mobile Solutions (Centrica plc) — cancelled project ──
  {
    id: 'b337', resourceId: '2', projectId: 'proj-008', projectName: 'UK Mobile Solutions',
    description: 'UX prototype — iOS', startTime: getFullDayStart(-90), endTime: getMultiDayEnd(-90, 3),
    color: getProjectColor('UK Mobile Solutions'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Centrica plc', location: 'Remote',
    details: 'iOS wireframes and interactive prototype',
  },
  {
    id: 'b338', resourceId: '7', projectId: 'proj-008', projectName: 'UK Mobile Solutions',
    description: 'Android prototype build', startTime: getFullDayStart(-80), endTime: getMultiDayEnd(-80, 2),
    color: getProjectColor('UK Mobile Solutions'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'Europe/London', customer: 'Centrica plc', location: 'Remote',
    details: 'Android prototype — project subsequently cancelled',
  },

  // ── proj-009: Internal Platform Upgrade ──
  {
    id: 'b339', resourceId: '6', projectId: 'proj-009', projectName: 'Internal Platform Upgrade',
    description: 'CI/CD pipeline migration', startTime: getFullDayStart(-40), endTime: getMultiDayEnd(-40, 3),
    color: getProjectColor('Internal Platform Upgrade'), durationType: 'full-day', billingClassification: 'internal',
    workType: 'remote', status: 'completed', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-4', timezone: 'Europe/London', customer: 'Internal', location: 'Remote',
    details: 'GitHub Actions migration from Jenkins — completed',
  },
  {
    id: 'b340', resourceId: '6', projectId: 'proj-009', projectName: 'Internal Platform Upgrade',
    description: 'Monitoring stack setup', startTime: getFullDayStart(3), endTime: getMultiDayEnd(3, 3),
    color: getProjectColor('Internal Platform Upgrade'), durationType: 'full-day', billingClassification: 'internal',
    workType: 'remote', status: 'confirmed', priority: 'medium', notifyReminders: true,
    bookingTypeId: 'bt-4', timezone: 'Europe/London', customer: 'Internal', location: 'Remote',
    details: 'Grafana + Prometheus monitoring stack',
  },
  {
    id: 'b341', resourceId: 'unallocated', projectId: 'proj-009', projectName: 'Internal Platform Upgrade',
    description: 'Developer portal build', startTime: getFullDayStart(20), endTime: getMultiDayEnd(20, 5),
    color: getProjectColor('Internal Platform Upgrade'), durationType: 'full-day', billingClassification: 'internal',
    workType: 'remote', status: 'planned', priority: 'low', notifyReminders: false,
    bookingTypeId: 'bt-4', timezone: 'Europe/London', customer: 'Internal', location: 'Remote',
    requiredRole: 'Frontend Developer', requiredSkills: ['React', 'TypeScript', 'Storybook'],
    details: 'Internal developer portal — resource needed',
  },

  // ── proj-010: USA Customer Platform (Salesforce Inc.) ──
  {
    id: 'b342', resourceId: '3', projectId: 'proj-010', projectName: 'USA Customer Platform',
    description: 'Project kickoff & scoping', startTime: getDateTimeOffset(7, 10), endTime: getDateTimeOffset(7, 16),
    color: getProjectColor('USA Customer Platform'), durationType: 'hourly', billingClassification: 'billable',
    workType: 'onsite', status: 'confirmed', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-4', timezone: 'America/Chicago', customer: 'Salesforce Inc.', location: 'San Francisco HQ',
    details: 'Discovery and scoping workshop with Salesforce stakeholders',
  },
  {
    id: 'b343', resourceId: '1', projectId: 'proj-010', projectName: 'USA Customer Platform',
    description: 'CRM frontend architecture', startTime: getFullDayStart(16), endTime: getMultiDayEnd(16, 3),
    color: getProjectColor('USA Customer Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'high', notifyReminders: true,
    bookingTypeId: 'bt-1', timezone: 'America/Chicago', customer: 'Salesforce Inc.', location: 'Remote',
    details: 'React component architecture for CRM views',
  },
  {
    id: 'b344', resourceId: 'unallocated', projectId: 'proj-010', projectName: 'USA Customer Platform',
    description: 'CRM backend API build', startTime: getFullDayStart(16), endTime: getMultiDayEnd(16, 5),
    color: getProjectColor('USA Customer Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Chicago', customer: 'Salesforce Inc.', location: 'Remote',
    requiredRole: 'Backend Developer', requiredSkills: ['Node.js', 'PostgreSQL', 'REST APIs'],
    details: 'Core CRM API services — resource needed',
  },
  {
    id: 'b345', resourceId: 'unallocated', projectId: 'proj-010', projectName: 'USA Customer Platform',
    description: 'Data migration strategy', startTime: getFullDayStart(22), endTime: getMultiDayEnd(22, 3),
    color: getProjectColor('USA Customer Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'high', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Chicago', customer: 'Salesforce Inc.', location: 'Remote',
    requiredRole: 'Data Engineer', requiredSkills: ['ETL', 'SQL', 'Salesforce APIs'],
    details: 'Legacy CRM data migration planning and execution',
  },
  {
    id: 'b346', resourceId: 'unallocated', projectId: 'proj-010', projectName: 'USA Customer Platform',
    description: 'UX design — sales dashboard', startTime: getFullDayStart(28), endTime: getMultiDayEnd(28, 4),
    color: getProjectColor('USA Customer Platform'), durationType: 'full-day', billingClassification: 'billable',
    workType: 'remote', status: 'planned', priority: 'medium', notifyReminders: false,
    bookingTypeId: 'bt-1', timezone: 'America/Chicago', customer: 'Salesforce Inc.', location: 'Remote',
    requiredRole: 'UX Designer', requiredSkills: ['Figma', 'User Research', 'Dashboard Design'],
    details: 'Sales pipeline and activity dashboard UX',
  },

  // ===== SARAH JOHNSON OVERLOAD SCENARIO =====
  // Sarah (id '1') is stacked with Website Redesign full-week blocks across
  // weeks 1–3 plus a concurrent API Integration booking that pushes her
  // well over 110% utilisation — guaranteed to trigger the burnout alert.
  // Alex Morgan (id '16') has no bookings at all and will be selected as
  // the conflict-free candidate.

  // Week 1 (days 1–5) — Website Redesign full week
  {
    id: 'bsr-01',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Sprint 6 — full week delivery',
    startTime: getFullDayStart(1),
    endTime: getMultiDayEnd(1, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Sprint 6 full-week development block',
  },
  // Week 1 concurrent — API Integration (creates >110% for week 1)
  {
    id: 'bsr-02',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc1',
    description: 'Emergency API fixes',
    startTime: getDateTimeOffset(1, 9, 0),
    endTime: getDateTimeOffset(5, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Urgent API stability fixes alongside redesign sprint',
  },
  // Week 2 (days 7–11) — Website Redesign full week
  {
    id: 'bsr-03',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Sprint 7 — full week delivery',
    startTime: getFullDayStart(7),
    endTime: getMultiDayEnd(7, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Sprint 7 full-week development block',
  },
  // Week 2 concurrent — API Integration
  {
    id: 'bsr-04',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc1',
    description: 'API Integration week 2',
    startTime: getDateTimeOffset(7, 9, 0),
    endTime: getDateTimeOffset(11, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Continued API work alongside redesign sprint',
  },
  // Week 3 (days 14–18) — Website Redesign full week
  {
    id: 'bsr-05',
    resourceId: '1',
    projectId: 'pd1',
    projectName: 'Website Redesign',
    costCentreId: 'cc1',
    description: 'Sprint 8 — full week delivery',
    startTime: getFullDayStart(14),
    endTime: getMultiDayEnd(14, 5),
    color: getProjectColor('Website Redesign'),
    durationType: 'full-day',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Acme Corporation',
    location: 'Remote',
    details: 'Sprint 8 full-week development block',
  },
  // Week 3 concurrent — API Integration
  {
    id: 'bsr-06',
    resourceId: '1',
    projectId: 'pd3',
    projectName: 'API Integration',
    costCentreId: 'cc1',
    description: 'API Integration week 3',
    startTime: getDateTimeOffset(14, 9, 0),
    endTime: getDateTimeOffset(18, 17, 0),
    color: getProjectColor('API Integration'),
    durationType: 'hourly',
    billingClassification: 'billable',
    workType: 'remote',
    status: 'confirmed',
    priority: 'high',
    notifyReminders: true,
    bookingTypeId: 'bt-1',
    timezone: 'Europe/London',
    customer: 'Global Finance Ltd',
    location: 'Remote',
    details: 'Continued API work week 3',
  },
];

export const mockProjects: Project[] = [
  {
    id: 'pd1',
    name: 'Website Redesign',
    code: 'WR-2024-001',
    budgetDays: 40,
    consumedDays: 28,
    color: getProjectColor('Website Redesign'),
    description: 'Complete website redesign with modern UI/UX',
    status: 'active',
    clientId: 'c1',
    costCentreIds: ['cc1', 'cc3'],
    companyId: '1',
    roleBudgetHours: [
      { role: 'Frontend Developer', monthlyHours: { '2026-01': 90, '2026-02': 90, '2026-03': 90, '2026-04': 90, '2026-05': 90, '2026-06': 90, '2026-07': 90, '2026-08': 90, '2026-09': 90, '2026-10': 90, '2026-11': 90, '2026-12': 90 } },
      { role: 'UI/UX Designer',     monthlyHours: { '2026-01': 40, '2026-02': 40, '2026-03': 40, '2026-04': 40, '2026-05': 40, '2026-06': 40, '2026-07': 40, '2026-08': 40, '2026-09': 40, '2026-10': 40, '2026-11': 40, '2026-12': 40 } },
      { role: 'Project Manager',    monthlyHours: { '2026-01': 20, '2026-02': 20, '2026-03': 20, '2026-04': 20, '2026-05': 20, '2026-06': 20, '2026-07': 20, '2026-08': 20, '2026-09': 20, '2026-10': 20, '2026-11': 20, '2026-12': 20 } },
    ],
  },
  {
    id: 'pd2',
    name: 'Mobile App Design',
    code: 'MAD-2024-002',
    budgetDays: 30,
    consumedDays: 15,
    color: getProjectColor('Mobile App Design'),
    description: 'Native mobile application design and development',
    status: 'active',
    clientId: 'c3',
    costCentreIds: ['cc2', 'cc3'],
    companyId: '1',
    roleBudgetHours: [
      { role: 'UI/UX Designer',       monthlyHours: { '2026-01': 80, '2026-02': 80, '2026-03': 80, '2026-04': 80, '2026-05': 80, '2026-06': 80, '2026-07': 80, '2026-08': 80, '2026-09': 80, '2026-10': 80, '2026-11': 80, '2026-12': 80 } },
      { role: 'Frontend Developer',   monthlyHours: { '2026-01': 60, '2026-02': 60, '2026-03': 60, '2026-04': 60, '2026-05': 60, '2026-06': 60, '2026-07': 60, '2026-08': 60, '2026-09': 60, '2026-10': 60, '2026-11': 60, '2026-12': 60 } },
    ],
  },
  {
    id: 'pd3',
    name: 'API Integration',
    code: 'GFL-2024-015',
    budgetDays: 25,
    consumedDays: 26,
    color: getProjectColor('API Integration'),
    description: 'Integration with third-party payment and authentication APIs',
    status: 'active',
    clientId: 'c2',
    costCentreIds: ['cc2', 'cc5', 'cc7'],
    companyId: '2',
    roleBudgetHours: [
      { role: 'Backend Developer', monthlyHours: { '2026-01': 90, '2026-02': 90, '2026-03': 90, '2026-04': 90, '2026-05': 90, '2026-06': 90, '2026-07': 90, '2026-08': 90, '2026-09': 90, '2026-10': 90, '2026-11': 90, '2026-12': 90 } },
      { role: 'DevOps Engineer',   monthlyHours: { '2026-01': 30, '2026-02': 30, '2026-03': 30, '2026-04': 30, '2026-05': 30, '2026-06': 30, '2026-07': 30, '2026-08': 30, '2026-09': 30, '2026-10': 30, '2026-11': 30, '2026-12': 30 } },
    ],
  },
  {
    id: 'pd4',
    name: 'Database Migration',
    code: 'DM-2024-004',
    budgetDays: 20,
    consumedDays: 8,
    color: getProjectColor('Database Migration'),
    description: 'Migration from legacy database to modern cloud solution',
    status: 'active',
    clientId: 'c6',
    costCentreIds: ['cc2', 'cc6'],
    companyId: '2',
    roleBudgetHours: [
      { role: 'Backend Developer', monthlyHours: { '2026-01': 70, '2026-02': 70, '2026-03': 70, '2026-04': 70, '2026-05': 70, '2026-06': 70, '2026-07': 70, '2026-08': 70, '2026-09': 70, '2026-10': 70, '2026-11': 70, '2026-12': 70 } },
      { role: 'Senior Developer',  monthlyHours: { '2026-01': 50, '2026-02': 50, '2026-03': 50, '2026-04': 50, '2026-05': 50, '2026-06': 50, '2026-07': 50, '2026-08': 50, '2026-09': 50, '2026-10': 50, '2026-11': 50, '2026-12': 50 } },
    ],
  },
  {
    id: 'pd5',
    name: 'Testing Phase 2',
    code: 'TP2-2024-005',
    budgetDays: 15,
    consumedDays: 14,
    color: getProjectColor('Testing Phase 2'),
    description: 'Comprehensive testing and quality assurance phase 2',
    status: 'active',
    clientId: 'c5',
    costCentreIds: ['cc4', 'cc5'],
    companyId: '3',
    roleBudgetHours: [
      { role: 'QA Engineer', monthlyHours: { '2026-01': 80, '2026-02': 80, '2026-03': 80, '2026-04': 80, '2026-05': 80, '2026-06': 80, '2026-07': 80, '2026-08': 80, '2026-09': 80, '2026-10': 80, '2026-11': 80, '2026-12': 80 } },
    ],
  },
  {
    id: 'pd6',
    name: 'Security Audit',
    code: 'SA-2024-006',
    budgetDays: 50,
    consumedDays: 32,
    color: getProjectColor('Security Audit'),
    description: 'Comprehensive security audit and penetration testing',
    status: 'active',
    clientId: 'c4',
    costCentreIds: ['cc1', 'cc12'],
    companyId: '3',
  },
  {
    id: 'pd7',
    name: 'Performance Optimization',
    code: 'PO-2024-007',
    budgetDays: 35,
    consumedDays: 18,
    color: getProjectColor('Performance Optimization'),
    description: 'Application performance optimization and speed improvements',
    status: 'active',
    clientId: 'c1',
    costCentreIds: ['cc2', 'cc6', 'cc11'],
    companyId: '4',
  },
  {
    id: 'pd8',
    name: 'Sprint Planning',
    code: 'SP-2024-008',
    budgetDays: 20,
    consumedDays: 12,
    color: getProjectColor('Sprint Planning'),
    description: 'Quarterly sprint planning and roadmap development',
    status: 'active',
    clientId: 'c7',
    costCentreIds: ['cc3', 'cc4', 'cc13'],
    companyId: '4',
  },
  {
    id: 'pd9',
    name: 'Customer Portal',
    code: 'HTS-2024-013',
    budgetDays: 45,
    consumedDays: 38,
    color: getProjectColor('Customer Portal'),
    description: 'Self-service customer portal with account management',
    status: 'active',
    clientId: 'c3',
    costCentreIds: ['cc2', 'cc6', 'cc10'],
    companyId: '5',
  },
  {
    id: 'pd10',
    name: 'Marketing Website',
    code: 'GFL-2024-020',
    budgetDays: 25,
    consumedDays: 22,
    color: getProjectColor('Marketing Website'),
    description: 'New marketing website with CMS integration',
    status: 'estimate',
    clientId: 'c2',
    costCentreIds: ['cc8', 'cc3', 'cc14'],
    companyId: '5',
  },
];

export const defaultSettings: AppSettings = {
  defaultCalendarView: 'day',
  businessHoursStart: 8,
  businessHoursEnd: 18,
  visibleHoursStart: 7,
  visibleHoursEnd: 19,
  timeSlotInterval: 30,
  // Global Working Hours Profile
  globalWorkingProfile: {
    monday: 7.5,
    tuesday: 7.5,
    wednesday: 7.5,
    thursday: 7.5,
    friday: 7.5,
    saturday: 0,
    sunday: 0,
  },
  // Administrator Settings
  showAbsence: true,
  absenceLabelOverride: undefined,
  absenceColor: '#94a3b8',
  showPublicHolidays: true,
  publicHolidayColor: '#fbbf24',
  groupingMethod: 'scheduler-group',
  confirmDragDrop: false, // Default to no confirmation (users can enable if desired)
  defaultToLoggedInResource: false,
  doNotCopyUserFields: false,
  showResourceRates: true,
  copyResourceRates: true,
  defaultTimezone: 'Europe/London', // UK timezone as default
  enableTimezones: false, // Multiple timezones disabled by default
  availableTimezones: [], // Empty when timezones are disabled
  quickAddDefault: 'bt-1', // Default to 'Project' booking type
  descriptionDefault: 'none',
  availableProjectStatuses: ['active', 'reopened'],
  availableCostCentreStatuses: ['active', 'reopened'],
  statusConfigs: [
    { id: 'planned', text: 'Planned', color: '#86efac', enabled: true },
    { id: 'confirmed', text: 'Confirmed', color: '#c084fc', enabled: true },
    { id: 'cancelled', text: 'Cancelled', color: '#e5e5e5', enabled: true },
    { id: 'completed', text: 'Completed', color: '#5eead4', enabled: true },
  ],
  colorOverrides: [
    { id: 'override-1', text: 'Override1', color: '#22c55e', enabled: true },
    { id: 'override-2', text: 'Override 2', color: '#f87171', enabled: true },
    { id: 'override-3', text: 'Override Text', color: '#d1d5db', enabled: true },
  ],
  utilisationTiers: [
    { id: 'tier-1', label: 'Available',     color: '#bfdbfe', minPercentage: 0,   maxPercentage: 69,  enabled: true },
    { id: 'tier-2', label: 'On Track',      color: '#bbf7d0', minPercentage: 70,  maxPercentage: 84,  enabled: true },
    { id: 'tier-3', label: 'Near Capacity', color: '#fde68a', minPercentage: 85,  maxPercentage: 100, enabled: true },
    // Overloaded starts at 101% — a resource booked at exactly 100% is at capacity, not overloaded.
    { id: 'tier-4', label: 'Overloaded',    color: '#fecaca', minPercentage: 101, maxPercentage: 999, enabled: true },
  ],
  // Forecast views (Month/Quarter/Year): planned-only cells at or above this % get the corner triangle.
  // Below this threshold, the lighter opacity + dashed border treatment alone is used.
  plannedOnlyWarningThreshold: 70, // kept for backwards compat — forecastSettings takes precedence
  forecastSettings: {
    monthThreshold: 70,
    quarterThreshold: 60,
    yearThreshold: 50,
    showPlannedPrefix: true,

    showConfirmedPlannedSplit: true,
  },
  bookingTypes: [
    {
      id: 'bt-1',
      code: 'PROJECT',
      name: 'Project',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: generateDefaultTabConfigurations(),
      companyId: '1',
    },
    {
      id: 'bt-2',
      code: 'ABSENCE',
      name: 'Absence',
      active: true,
      autoUpdateTimesheet: false,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: false,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: true,
      allowRepeatBookings: false,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: '', isVisible: false },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: true },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: '', isVisible: false },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: '', isVisible: false },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: '2',
    },
    {
      id: 'bt-3',
      code: 'TRAINING',
      name: 'Training',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: true,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: true,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: 'Training Location', isVisible: true },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: true },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: 'Attendee Notes', isVisible: true },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: 'Training Notes', isVisible: true },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: '3',
    },
    {
      id: 'bt-4',
      code: 'MEETING',
      name: 'Meeting',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: false,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: [
        { tabName: 'address', defaultName: 'Address', customName: 'Meeting Room', isVisible: true },
        { tabName: 'history', defaultName: 'History', customName: '', isVisible: false },
        { tabName: 'customer-notes', defaultName: 'Customer Notes', customName: 'Agenda', isVisible: true },
        { tabName: 'project-notes', defaultName: 'Project Notes', customName: 'Meeting Notes', isVisible: true },
        { tabName: 'custom', defaultName: 'Custom', customName: '', isVisible: false },
      ],
      companyId: '4',
    },
    {
      id: 'bt-5',
      code: 'INTERNAL',
      name: 'Internal Work',
      active: true,
      autoUpdateTimesheet: true,
      doNotUpdateTimesheetWithHours: false,
      obeyUserWorkingProfileAndBankHolidays: true,
      autoAdjustBookingForWorkingProfileAndBankHolidays: false,
      addDaysToEndOfBooking: false,
      allowRepeatBookings: true,
      fieldConfigurations: generateLegacyFieldConfigurations(),
      fieldOrder: generateDefaultFieldOrder(),
      tabConfigurations: generateDefaultTabConfigurations(),
      companyId: '5',
    },
  ],
  defaultBookingTypeId: 'bt-1', // MODIFICATION: Set default booking type to 'Project'
  resourcePermissions: [], // Initialize with empty permissions array
  // Timeline Options
  timelineDefaultTimeRange: 'Week',
  timelineDefaultBookingType: 'bt-1', // Use booking type ID instead of hardcoded string
  timelineDefaultBookingStatus: 'planned',
  timelineDefaultView: 'resource',
  timelineDefaultTimezone: 'Europe/London',
  calendarSyncUrl: '',
  // User Preferences (per-user overrides, undefined means use global default)
  userConfirmDragDrop: undefined,
  userDefaultToLoggedInResource: undefined,
  // Absence Integration
  absenceIntegrationEnabled: false,
  absenceApiUrl: '',
  absenceApiKey: '',
  absenceSyncInterval: 60, // 60 minutes default
  absenceAutoSync: false,
  companyId: '1',
};

// Per-company settings overrides — in live mode appSettingsApi.getAll() returns
// company-specific settings via the X-Company-Id header. Mirror this in mock
// mode so each company can have its own configuration.
export const MOCK_COMPANY_SETTINGS: Record<string, AppSettings> = {
  '1': defaultSettings,
  '2': {
    ...defaultSettings,
    companyId: '2',
    defaultCalendarView: 'week',
    groupingMethod: 'department',
    defaultToLoggedInResource: true,
    quickAddDefault: 'bt-2',
    defaultBookingTypeId: 'bt-2',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === '2'),
  },
  '3': {
    ...defaultSettings,
    companyId: '3',
    defaultCalendarView: 'week',
    confirmDragDrop: true,
    enableTimezones: true,
    availableTimezones: ['Europe/London', 'America/New_York', 'Asia/Tokyo'],
    quickAddDefault: 'bt-3',
    defaultBookingTypeId: 'bt-3',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === '3'),
  },
  '4': {
    ...defaultSettings,
    companyId: '4',
    defaultCalendarView: 'day',
    businessHoursStart: 9,
    businessHoursEnd: 17,
    visibleHoursStart: 8,
    visibleHoursEnd: 18,
    quickAddDefault: 'bt-4',
    defaultBookingTypeId: 'bt-4',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === '4'),
  },
  '5': {
    ...defaultSettings,
    companyId: '5',
    defaultCalendarView: 'month',
    showAbsence: false,
    showPublicHolidays: false,
    quickAddDefault: 'bt-5',
    defaultBookingTypeId: 'bt-5',
    bookingTypes: defaultSettings.bookingTypes.filter(bt => bt.companyId === '5'),
  },
};

// Export mockBookingTypes for use in SchedulerContext
export const mockBookingTypes = defaultSettings.bookingTypes;

// Mock Clients with UK Addresses
export const mockClients: Client[] = [
  { 
    id: 'c1', 
    name: 'Acme Corporation', 
    code: 'ACME-001',
    industry: 'Technology',
    streetAddress: '45 Broadwick Street',
    addressLine2: 'Floor 3, Soho Works',
    city: 'London',
    county: 'Greater London',
    postcode: 'W1F 9QZ',
    country: 'United Kingdom',
    mobileNumber: '07700 900123',
    phoneNumber: '020 7946 0958',
    email: 'info@acmecorp.co.uk',
    companyId: '1',
  },
  { 
    id: 'c2', 
    name: 'Global Finance Ltd', 
    code: 'GFL-002',
    industry: 'Financial Services',
    streetAddress: '123 Deansgate',
    addressLine2: 'Suite 12, Deansgate Square',
    city: 'Manchester',
    county: 'Greater Manchester',
    postcode: 'M3 2BW',
    country: 'United Kingdom',
    mobileNumber: '07700 900456',
    phoneNumber: '0161 496 0123',
    email: 'contact@globalfinance.co.uk',
    companyId: '2',
  },
  { 
    id: 'c3', 
    name: 'HealthTech Solutions', 
    code: 'HTS-003',
    industry: 'Healthcare',
    streetAddress: '78 George Street',
    city: 'Edinburgh',
    county: 'Midlothian',
    postcode: 'EH2 3BU',
    country: 'United Kingdom',
    mobileNumber: '07700 900789',
    phoneNumber: '0131 496 0234',
    email: 'enquiries@healthtechsolutions.co.uk',
    companyId: '3',
  },
  { 
    id: 'c4', 
    name: 'Retail Innovations Inc', 
    code: 'RII-004',
    industry: 'Retail',
    streetAddress: '56 Park Street',
    city: 'Bristol',
    county: 'Bristol',
    postcode: 'BS1 5JN',
    country: 'United Kingdom',
    mobileNumber: '07700 900321',
    phoneNumber: '0117 496 0345',
    email: 'hello@retailinnovations.co.uk',
    companyId: '3',
  },
  { 
    id: 'c5', 
    name: 'EduLearn Platform', 
    code: 'ELP-005',
    industry: 'Education',
    streetAddress: '92 Bold Street',
    city: 'Liverpool',
    county: 'Merseyside',
    postcode: 'L1 4HY',
    country: 'United Kingdom',
    mobileNumber: '07700 900654',
    phoneNumber: '0151 496 0456',
    email: 'support@edulearn.co.uk',
    companyId: '4',
  },
  { 
    id: 'c6', 
    name: 'GreenEnergy Co', 
    code: 'GEC-006',
    industry: 'Energy',
    streetAddress: '34 Corporation Street',
    city: 'Birmingham',
    county: 'West Midlands',
    postcode: 'B2 4LP',
    country: 'United Kingdom',
    mobileNumber: '07700 900987',
    phoneNumber: '0121 496 0567',
    email: 'info@greenenergy.co.uk',
    companyId: '4',
  },
  { 
    id: 'c7', 
    name: 'Internal', 
    code: 'INT-007',
    industry: 'Internal',
    streetAddress: '10 Downing Street',
    city: 'London',
    county: 'Greater London',
    postcode: 'SW1A 2AA',
    country: 'United Kingdom',
    mobileNumber: '07700 900111',
    phoneNumber: '020 7946 0100',
    email: 'admin@internal.co.uk',
    companyId: '5',
  },
];

// Mock Cost Centres
export const mockCostCentres: CostCentre[] = [
  { id: 'cc1', name: 'Engineering - Frontend', code: 'ENG-FE-001', description: 'Frontend development and UI engineering', department: 'Technology', status: 'active', userSubtotal: 'US-ENG-FE', companyId: '1' },
  { id: 'cc2', name: 'Engineering - Backend', code: 'ENG-BE-001', description: 'Backend services and API development', department: 'Technology', status: 'active', userSubtotal: 'US-ENG-BE', companyId: '1' },
  { id: 'cc3', name: 'Design & UX', code: 'DES-001', description: 'User experience design and visual design', department: 'Creative', status: 'active', userSubtotal: 'US-DES', companyId: '1' },
  { id: 'cc4', name: 'Project Management', code: 'PM-001', description: 'Project planning, tracking and delivery', department: 'Operations', status: 'active', userSubtotal: 'US-PM', companyId: '2' },
  { id: 'cc5', name: 'Quality Assurance', code: 'QA-001', description: 'Testing and quality control', department: 'Technology', status: 'active', userSubtotal: 'UK-QA', companyId: '2' },
  { id: 'cc6', name: 'DevOps & Infrastructure', code: 'OPS-001', description: 'Infrastructure management and CI/CD', department: 'Infrastructure', status: 'active', userSubtotal: 'UK-OPS', companyId: '2' },
  { id: 'cc7', name: 'Business Analysis', code: 'BA-001', description: 'Requirements gathering and business process analysis', department: 'Strategy', status: 'active', userSubtotal: 'CA-BA', companyId: '3' },
  { id: 'cc8', name: 'Marketing & Communications', code: 'MKT-001', description: 'Marketing campaigns and corporate communications', department: 'Marketing', status: 'active', userSubtotal: 'US-MKT', companyId: '3' },
  { id: 'cc9', name: 'Research & Development', code: 'RND-001', description: 'Innovation and new technology research', department: 'Innovation', status: 'active', userSubtotal: 'UK-RND', companyId: '3' },
  { id: 'cc10', name: 'Customer Support', code: 'SUP-001', description: 'Customer service and technical support', department: 'Support', status: 'active', userSubtotal: 'CA-SUP', companyId: '4' },
  { id: 'cc11', name: 'Data Analytics', code: 'DATA-001', description: 'Data analysis and business intelligence', department: 'Technology', status: 'active', userSubtotal: 'US-DATA', companyId: '4' },
  { id: 'cc12', name: 'Security & Compliance', code: 'SEC-001', description: 'Information security and regulatory compliance', department: 'Technology', status: 'active', userSubtotal: 'UK-SEC', companyId: '4' },
  { id: 'cc13', name: 'Product Management', code: 'PROD-001', description: 'Product strategy and roadmap management', department: 'Product', status: 'active', userSubtotal: 'US-PROD', companyId: '5' },
  { id: 'cc14', name: 'Sales & Business Development', code: 'SALES-001', description: 'Sales operations and new business acquisition', department: 'Sales', status: 'active', userSubtotal: 'US-SALES', companyId: '5' },
  { id: 'cc15', name: 'Finance & Administration', code: 'FIN-001', description: 'Financial management and office administration', department: 'Finance', status: 'active', userSubtotal: 'UK-FIN', companyId: '5' },
];

/**
 * Project to Cost Centre Mapping
 * 
 * This mapping defines which cost centres are associated with each project.
 * Used to implement dependent field behavior where:
 * - Cost Centre field is disabled until a Project is selected
 * - Only cost centres associated with the selected project are shown
 * - When changing projects, the cost centre is cleared UNLESS it's shared with the new project
 * 
 * Mock Data Structure:
 * - 5 projects (pd1-pd5) with 3-5 cost centres each
 * - Shared cost centres: cc1 (Engineering) and cc3 (Project Management) appear in multiple projects
 * - Unique cost centres: cc8 (Marketing) only in pd2, cc9 (R&D) only in pd3
 * 
 * Test Scenarios:
 * 1. Initial state: Cost Centre disabled, no project selected
 * 2. Select pd1 (Website Redesign): Cost Centre enables, shows cc1, cc2, cc3, cc4
 * 3. Select cc1 (Engineering) from pd1
 * 4. Change to pd3 (API Integration) which also has cc1: Cost centre value remains (cc1 is shared)
 * 5. Change to pd2 (Mobile App Design) which doesn't have cc1: Cost centre clears
 */
export const projectCostCentreMapping: Record<string, string[]> = {
  // Website Redesign - Engineering Frontend/Backend, Design, Project Management
  'pd1': ['cc1', 'cc2', 'cc3', 'cc4'],
  
  // Mobile App Design - Engineering Frontend, Design & UX, QA, Project Management
  'pd2': ['cc1', 'cc3', 'cc4', 'cc5'],
  
  // API Integration - Engineering Frontend/Backend, DevOps, Project Management
  'pd3': ['cc1', 'cc2', 'cc4', 'cc6'],
  
  // Database Migration - Engineering Backend, DevOps, QA, Project Management
  'pd4': ['cc2', 'cc4', 'cc5', 'cc6'],
  
  // Testing Phase 2 - QA, Engineering Frontend/Backend, Project Management
  'pd5': ['cc1', 'cc2', 'cc4', 'cc5'],
  
  // Security Audit - Security & Compliance, DevOps, Project Management
  'pd6': ['cc4', 'cc6', 'cc12'],
  
  // Performance Optimization - Engineering Frontend/Backend, DevOps, Project Management
  'pd7': ['cc1', 'cc2', 'cc4', 'cc6'],
  
  // Sprint Planning - Project Management, Business Analysis
  'pd8': ['cc3', 'cc4', 'cc7'],
  
  // Customer Portal - Engineering Frontend/Backend, Customer Support, Project Management
  'pd9': ['cc1', 'cc2', 'cc4', 'cc10'],
  
  // Marketing Website - Engineering Frontend, Design & UX, Marketing, Project Management
  'pd10': ['cc1', 'cc3', 'cc4', 'cc8'],
};

/**
 * Helper function to get cost centres for a specific project
 * @param projectId - The project ID to get cost centres for
 * @returns Array of cost centre IDs associated with the project, or empty array if no mapping exists
 */
export function getCostCentresForProject(projectId: string): string[] {
  return projectCostCentreMapping[projectId] || [];
}

/**
 * Helper function to check if a cost centre is associated with a project
 * @param projectId - The project ID to check
 * @param costCentreId - The cost centre ID to check
 * @returns true if the cost centre is associated with the project
 */
export function isCostCentreInProject(projectId: string, costCentreId: string): boolean {
  const costCentres = projectCostCentreMapping[projectId];
  return costCentres ? costCentres.includes(costCentreId) : false;
}

/**
 * Helper function to get the customer (client) ID for a specific project
 * @param projectId - The project ID to get the customer for
 * @returns The client ID associated with the project, or undefined if not found
 */
export function getCustomerForProject(projectId: string): string | undefined {
  const project = mockProjectDetails.find(p => String(p.id) === String(projectId));
  return project?.clientId != null ? String(project.clientId) : undefined;
}

/**
 * Helper function to get projects for a specific customer (client)
 * @param customerId - The customer/client ID to get projects for
 * @returns Array of project IDs associated with the customer
 */
export function getProjectsForCustomer(customerId: string): string[] {
  return mockProjectDetails
    .filter(p => String(p.clientId) === String(customerId))
    .map(p => String(p.id));
}

/**
 * Helper function to check if a project belongs to a specific customer
 * @param projectId - The project ID to check
 * @param customerId - The customer/client ID to check
 * @returns true if the project belongs to the customer
 */
export function isProjectForCustomer(projectId: string, customerId: string): boolean {
  const project = mockProjectDetails.find(p => String(p.id) === String(projectId));
  return String(project?.clientId) === String(customerId);
}

// Mock Absences - various types for different resources across 8 weeks
// Reusing the 'today' variable declared at the top of the file

// Helper function to get date offset from today
const getDateOffset = (days: number): string => {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

export const mockAbsences: Absence[] = [];

/* TEMPORARILY DISABLED - Absence data removed to prevent ID collision errors
export const mockAbsences_DISABLED: Absence[] = [
  // ===== WEEK 1 (Days 0-6) =====
  // Today - David Kim full day annual leave
  {
    id: 'abs-1',
    resourceId: '4',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(0),
    endDate: getDateOffset(0),
  },
  // Today - Emily Rodriguez hourly dentist appointment
  {
    id: 'abs-2',
    resourceId: '3',
    type: 'hourly',
    reason: 'Dentist Appointment',
    startDate: getDateOffset(0),
    endDate: getDateOffset(0),
    startTime: '14:00',
    endTime: '16:00',
  },
  // Tomorrow - Sarah Johnson half-day AM medical
  {
    id: 'abs-3',
    resourceId: '1',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(1),
    endDate: getDateOffset(1),
  },
  // Day 2 - Michael Chen half-day PM personal
  {
    id: 'abs-4',
    resourceId: '2',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(2),
    endDate: getDateOffset(2),
  },
  // Day 3 - Robert Taylor hourly school run
  {
    id: 'abs-5',
    resourceId: '6',
    type: 'hourly',
    reason: 'School Run',
    startDate: getDateOffset(3),
    endDate: getDateOffset(3),
    startTime: '08:00',
    endTime: '09:30',
  },
  // Day 4-5 - Amanda Martinez 2-day sick leave
  {
    id: 'abs-6',
    resourceId: '7',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(4),
    endDate: getDateOffset(5),
  },

  // ===== WEEK 2 (Days 7-13) =====
  // Day 7 - Jessica Williams full day annual leave
  {
    id: 'abs-7',
    resourceId: '5',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(7),
    endDate: getDateOffset(7),
  },
  // Day 8 - James Anderson half-day AM training
  {
    id: 'abs-8',
    resourceId: '8',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(8),
    endDate: getDateOffset(8),
  },
  // Day 9 - Sarah Johnson hourly doctor appointment
  {
    id: 'abs-9',
    resourceId: '1',
    type: 'hourly',
    reason: 'Doctor Appointment',
    startDate: getDateOffset(9),
    endDate: getDateOffset(9),
    startTime: '10:00',
    endTime: '11:30',
  },
  // Day 10-12 - David Kim 3-day holiday
  {
    id: 'abs-10',
    resourceId: '4',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(10),
    endDate: getDateOffset(12),
  },
  // Day 13 - Emily Rodriguez half-day PM personal
  {
    id: 'abs-11',
    resourceId: '3',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(13),
    endDate: getDateOffset(13),
  },

  // ===== WEEK 3 (Days 14-20) =====
  // Day 14 - Michael Chen full day sick leave
  {
    id: 'abs-12',
    resourceId: '2',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(14),
    endDate: getDateOffset(14),
  },
  // Day 15 - Robert Taylor hourly car service
  {
    id: 'abs-13',
    resourceId: '6',
    type: 'hourly',
    reason: 'Car Service',
    startDate: getDateOffset(15),
    endDate: getDateOffset(15),
    startTime: '13:00',
    endTime: '15:00',
  },
  // Day 16-17 - Amanda Martinez 2-day annual leave
  {
    id: 'abs-14',
    resourceId: '7',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(16),
    endDate: getDateOffset(17),
  },
  // Day 18 - Jessica Williams half-day AM medical
  {
    id: 'abs-15',
    resourceId: '5',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(18),
    endDate: getDateOffset(18),
  },
  // Day 19 - James Anderson hourly bank appointment
  {
    id: 'abs-16',
    resourceId: '8',
    type: 'hourly',
    reason: 'Bank Appointment',
    startDate: getDateOffset(19),
    endDate: getDateOffset(19),
    startTime: '09:00',
    endTime: '10:00',
  },

  // ===== WEEK 4 (Days 21-27) =====
  // Day 21 - Sarah Johnson full day annual leave
  {
    id: 'abs-17',
    resourceId: '1',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(21),
    endDate: getDateOffset(21),
  },
  // Day 22-24 - Michael Chen 3-day holiday
  {
    id: 'abs-18',
    resourceId: '2',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(22),
    endDate: getDateOffset(24),
  },
  // Day 25 - Emily Rodriguez hourly physio
  {
    id: 'abs-19',
    resourceId: '3',
    type: 'hourly',
    reason: 'Physiotherapy',
    startDate: getDateOffset(25),
    endDate: getDateOffset(25),
    startTime: '15:00',
    endTime: '16:30',
  },
  // Day 26 - David Kim half-day PM personal
  {
    id: 'abs-20',
    resourceId: '4',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(26),
    endDate: getDateOffset(26),
  },
  // Day 27 - Robert Taylor full day sick leave
  {
    id: 'abs-21',
    resourceId: '6',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(27),
    endDate: getDateOffset(27),
  },

  // ===== WEEK 5 (Days 28-34) =====
  // Day 28-29 - Jessica Williams 2-day annual leave
  {
    id: 'abs-22',
    resourceId: '5',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(28),
    endDate: getDateOffset(29),
  },
  // Day 30 - Amanda Martinez half-day AM training
  {
    id: 'abs-23',
    resourceId: '7',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(30),
    endDate: getDateOffset(30),
  },
  // Day 31 - James Anderson hourly dentist
  {
    id: 'abs-24',
    resourceId: '8',
    type: 'hourly',
    reason: 'Dentist Appointment',
    startDate: getDateOffset(31),
    endDate: getDateOffset(31),
    startTime: '11:00',
    endTime: '12:00',
  },
  // Day 32 - Sarah Johnson half-day PM personal
  {
    id: 'abs-25',
    resourceId: '1',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(32),
    endDate: getDateOffset(32),
  },
  // Day 33-34 - Michael Chen 2-day sick leave
  {
    id: 'abs-26',
    resourceId: '2',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(33),
    endDate: getDateOffset(34),
  },

  // ===== WEEK 6 (Days 35-41) =====
  // Day 35 - Emily Rodriguez full day annual leave
  {
    id: 'abs-27',
    resourceId: '3',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(35),
    endDate: getDateOffset(35),
  },
  // Day 36 - David Kim hourly optician
  {
    id: 'abs-28',
    resourceId: '4',
    type: 'hourly',
    reason: 'Optician Appointment',
    startDate: getDateOffset(36),
    endDate: getDateOffset(36),
    startTime: '14:00',
    endTime: '15:30',
  },
  // Day 37-39 - Robert Taylor 3-day holiday
  {
    id: 'abs-29',
    resourceId: '6',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(37),
    endDate: getDateOffset(39),
  },
  // Day 40 - Amanda Martinez half-day AM medical
  {
    id: 'abs-30',
    resourceId: '7',
    type: 'half-day-am',
    reason: 'Medical Appointment',
    startDate: getDateOffset(40),
    endDate: getDateOffset(40),
  },
  // Day 41 - Jessica Williams hourly parent meeting
  {
    id: 'abs-31',
    resourceId: '5',
    type: 'hourly',
    reason: 'Parent Meeting',
    startDate: getDateOffset(41),
    endDate: getDateOffset(41),
    startTime: '16:00',
    endTime: '17:00',
  },

  // ===== WEEK 7 (Days 42-48) =====
  // Day 42 - James Anderson full day annual leave
  {
    id: 'abs-32',
    resourceId: '8',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(42),
    endDate: getDateOffset(42),
  },
  // Day 43 - Sarah Johnson hourly solicitor
  {
    id: 'abs-33',
    resourceId: '1',
    type: 'hourly',
    reason: 'Solicitor Appointment',
    startDate: getDateOffset(43),
    endDate: getDateOffset(43),
    startTime: '10:30',
    endTime: '12:00',
  },
  // Day 44-45 - Michael Chen 2-day annual leave
  {
    id: 'abs-34',
    resourceId: '2',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(44),
    endDate: getDateOffset(45),
  },
  // Day 46 - Emily Rodriguez half-day PM personal
  {
    id: 'abs-35',
    resourceId: '3',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(46),
    endDate: getDateOffset(46),
  },
  // Day 47 - David Kim full day sick leave
  {
    id: 'abs-36',
    resourceId: '4',
    type: 'full-day',
    reason: 'Sick Leave',
    startDate: getDateOffset(47),
    endDate: getDateOffset(47),
  },

  // ===== WEEK 8 (Days 49-55) =====
  // Day 49 - Robert Taylor half-day AM training
  {
    id: 'abs-37',
    resourceId: '6',
    type: 'half-day-am',
    reason: 'Training',
    startDate: getDateOffset(49),
    endDate: getDateOffset(49),
  },
  // Day 50-52 - Amanda Martinez 3-day holiday
  {
    id: 'abs-38',
    resourceId: '7',
    type: 'full-day',
    reason: 'Holiday',
    startDate: getDateOffset(50),
    endDate: getDateOffset(52),
  },
  // Day 53 - Jessica Williams hourly vet appointment
  {
    id: 'abs-39',
    resourceId: '5',
    type: 'hourly',
    reason: 'Vet Appointment',
    startDate: getDateOffset(53),
    endDate: getDateOffset(53),
    startTime: '13:30',
    endTime: '14:30',
  },
  // Day 54 - James Anderson half-day PM personal
  {
    id: 'abs-40',
    resourceId: '8',
    type: 'half-day-pm',
    reason: 'Personal',
    startDate: getDateOffset(54),
    endDate: getDateOffset(54),
  },
  // Day 55 - Sarah Johnson full day annual leave
  {
    id: 'abs-41',
    resourceId: '1',
    type: 'full-day',
    reason: 'Annual Leave',
    startDate: getDateOffset(55),
    endDate: getDateOffset(55),
  },
];
*/

// Mock Bank Holidays - spread across the 8-week period and beyond
export const mockBankHolidays: BankHoliday[] = [];

/* TEMPORARILY DISABLED - Bank holiday data removed to prevent ID collision errors
export const mockBankHolidays_DISABLED: BankHoliday[] = [
  // Current year bank holidays (UK) - using dynamic dates relative to today
  { id: 'bh-1', name: "Team Building Day", date: getDateOffset(5), country: 'GB' },
  { id: 'bh-2', name: 'Company Anniversary', date: getDateOffset(14), country: 'GB' },
  { id: 'bh-3', name: 'Regional Holiday', date: getDateOffset(28), country: 'GB' },
  { id: 'bh-4', name: 'National Day', date: getDateOffset(42), country: 'GB' },
  
  // Fixed UK bank holidays for 2026
  { id: 'bh-5', name: "New Year's Day", date: '2026-01-01', country: 'GB' },
  { id: 'bh-6', name: 'Good Friday', date: '2026-04-03', country: 'GB' },
  { id: 'bh-7', name: 'Easter Monday', date: '2026-04-06', country: 'GB' },
  { id: 'bh-8', name: 'Early May Bank Holiday', date: '2026-05-04', country: 'GB' },
  { id: 'bh-9', name: 'Spring Bank Holiday', date: '2026-05-25', country: 'GB' },
  { id: 'bh-10', name: 'Summer Bank Holiday', date: '2026-08-31', country: 'GB' },
  { id: 'bh-11', name: 'Christmas Day', date: '2026-12-25', country: 'GB' },
  { id: 'bh-12', name: 'Boxing Day', date: '2026-12-28', country: 'GB' },
];
*/

// Helper: resolve mock cost centres for a project using the projectCostCentreMapping
const getMockProjectCostCentres = (projectId: string): CostCentre[] => {
  const ccIds = projectCostCentreMapping[projectId] || [];
  return mockCostCentres.filter(cc => ccIds.includes(String(cc.id)));
};

// Mock Project Details
export const mockProjectDetails: ProjectDetails[] = [
  {
    id: 'pd1',
    name: 'Website Redesign',
    clientId: 'c1',
    clientName: 'Acme Corporation',
    billingCode: 'ACM-2024-001',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Website Redesign'),
    status: 'active',
    description: 'Complete redesign of company website with modern UI/UX',
    costCentres: getMockProjectCostCentres('pd1'),
    sortKey: 'ACM-001',
    userSort1: 'USA',
    userSort2: 'North America',
    userSort3: 'Enterprise',
    userChar1: 'Active',
    userChar2: 'High Priority',
    companyId: '1',
  },
  {
    id: 'pd2',
    name: 'Mobile App Design',
    clientId: 'c1',
    clientName: 'Acme Corporation',
    billingCode: 'ACM-2024-002',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Mobile App Design'),
    status: 'active',
    description: 'Design and prototype for iOS and Android mobile application',
    costCentres: getMockProjectCostCentres('pd2'),
    sortKey: 'ACM-002',
    userSort1: 'USA',
    userSort2: 'North America',
    userSort3: 'Mobile',
    userChar1: 'Active',
    userChar2: 'Medium Priority',
    companyId: '1',
  },
  {
    id: 'pd3',
    name: 'API Integration',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-015',
    projectManager: 'Michael Chen',
    color: getProjectColor('API Integration'),
    status: 'active',
    description: 'Integration with third-party payment and authentication APIs',
    costCentres: getMockProjectCostCentres('pd3'),
    sortKey: 'GFL-015',
    userSort1: 'UK',
    userSort2: 'Europe',
    userSort3: 'Integration',
    userChar1: 'Active',
    userChar2: 'High Priority',
    companyId: '2',
  },
  {
    id: 'pd4',
    name: 'Infrastructure Setup',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-016',
    projectManager: 'Robert Taylor',
    color: getProjectColor('Infrastructure Setup'),
    status: 'reopened',
    description: 'Cloud infrastructure setup and deployment automation',
    costCentres: getMockProjectCostCentres('pd4'),
    sortKey: 'GFL-016',
    userSort1: 'UK',
    userSort2: 'Europe',
    userSort3: 'Infrastructure',
    userChar1: 'Reopened',
    userChar2: 'Critical',
    companyId: '2',
  },
  {
    id: 'pd5',
    name: 'Testing Phase 2',
    clientId: 'c3',
    clientName: 'HealthTech Solutions',
    billingCode: 'HTS-2024-008',
    projectManager: 'Jessica Williams',
    color: getProjectColor('Testing Phase 2'),
    status: 'active',
    description: 'Comprehensive testing of all system components and integrations',
    costCentres: getMockProjectCostCentres('pd5'),
    sortKey: 'HTS-008',
    userSort1: 'Canada',
    userSort2: 'North America',
    userSort3: 'Testing',
    userChar1: 'Active',
    userChar2: 'Medium Priority',
    companyId: '3',
  },
  {
    id: 'pd6',
    name: 'Component Library',
    clientId: 'c4',
    clientName: 'Retail Innovations Inc',
    billingCode: 'RII-2024-022',
    projectManager: 'Amanda Martinez',
    color: getProjectColor('Component Library'),
    status: 'completed',
    costCentres: getMockProjectCostCentres('pd6'),
    sortKey: 'RII-022',
    userSort1: 'Germany',
    userSort2: 'Europe',
    userSort3: 'Development',
    userChar1: 'Completed',
    userChar2: 'Low Priority',
    companyId: '3',
  },
  {
    id: 'pd7',
    name: 'Brand Guidelines',
    clientId: 'c4',
    clientName: 'Retail Innovations Inc',
    billingCode: 'RII-2024-023',
    projectManager: 'Michael Chen',
    color: getProjectColor('Brand Guidelines'),
    status: 'estimate',
    costCentres: getMockProjectCostCentres('pd7'),
    companyId: '4',
  },
  {
    id: 'pd8',
    name: 'Sprint Planning',
    clientId: 'c5',
    clientName: 'EduLearn Platform',
    billingCode: 'ELP-2024-011',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Sprint Planning'),
    status: 'active',
    costCentres: getMockProjectCostCentres('pd8'),
    companyId: '4',
  },
  {
    id: 'pd9',
    name: 'Data Analytics Dashboard',
    clientId: 'c3',
    clientName: 'HealthTech Solutions',
    billingCode: 'HTS-2024-009',
    projectManager: 'James Anderson',
    color: getProjectColor('Data Analytics Dashboard'),
    status: 'opportunity',
    costCentres: getMockProjectCostCentres('pd9'),
    companyId: '5',
  },
  {
    id: 'pd10',
    name: 'Cloud Migration',
    clientId: 'c6',
    clientName: 'GreenEnergy Co',
    billingCode: 'GEC-2024-005',
    projectManager: 'Robert Taylor',
    color: getProjectColor('Cloud Migration'),
    status: 'suspended',
    costCentres: getMockProjectCostCentres('pd10'),
    companyId: '5',
  },
  {
    id: 'pd11',
    name: 'Security Audit',
    clientId: 'c2',
    clientName: 'Global Finance Ltd',
    billingCode: 'GFL-2024-017',
    projectManager: 'David Kim',
    color: getProjectColor('Security Audit'),
    status: 'cancelled',
    costCentres: getMockProjectCostCentres('pd11'),
    companyId: '2',
  },
  {
    id: 'pd12',
    name: 'Internal Training',
    clientId: 'c7',
    clientName: 'Internal',
    billingCode: 'INT-2024-001',
    projectManager: 'Emily Rodriguez',
    color: getProjectColor('Internal Training'),
    status: 'active',
    costCentres: getMockProjectCostCentres('pd12'),
    companyId: '5',
  },
];

// Company mappings for demo mode — distributes mock data across mock companies (IDs '1'–'5')
export const MOCK_RESOURCE_COMPANY_MAP: Record<string, string> = {
  'unallocated': '1',
  '1': '1', '2': '1', '3': '1', '4': '1',
  '5': '2', '6': '2', '7': '2',
  '8': '3', '9': '3', '10': '3',
  '11': '4', '12': '4', '13': '4',
  '14': '5', '15': '5', '16': '5',
};

export const MOCK_PROJECT_COMPANY_MAP: Record<string, string> = {
  'pd1': '1', 'pd2': '1',
  'pd3': '2', 'pd4': '2', 'pd11': '2',
  'pd5': '3', 'pd6': '3',
  'pd7': '4', 'pd8': '4',
  'pd9': '5', 'pd10': '5', 'pd12': '5',
};

export const MOCK_CLIENT_COMPANY_MAP: Record<string, string> = {
  'c1': '1', 'c2': '2',
  'c3': '3', 'c4': '3',
  'c5': '4', 'c6': '4',
  'c7': '5',
};

export const MOCK_COST_CENTRE_COMPANY_MAP: Record<string, string> = {
  'cc1': '1', 'cc2': '1', 'cc3': '1',
  'cc4': '2', 'cc5': '2', 'cc6': '2',
  'cc7': '3', 'cc8': '3', 'cc9': '3',
  'cc10': '4', 'cc11': '4', 'cc12': '4',
  'cc13': '5', 'cc14': '5', 'cc15': '5',
};

// Mock Scheduler Groups - groups of resources for organizing the scheduler view
// Each group is scoped to a company via companyId
export const mockSchedulerGroups = [
  {
    id: 'sg-1',
    name: 'Team Alpha',
    resourceIds: ['1', '3', '4', '9', '12'],
    avatarUrl: undefined,
    companyId: '1',
  },
  {
    id: 'sg-2',
    name: 'Team Beta',
    resourceIds: ['2', '6', '7', '10', '14', '15'],
    avatarUrl: undefined,
    companyId: '1',
  },
  {
    id: 'sg-3',
    name: 'Team Gamma',
    resourceIds: ['5', '8', '11', '13'],
    avatarUrl: undefined,
    companyId: '2',
  },
  {
    id: 'sg-system',
    name: 'System',
    resourceIds: ['unallocated'],
    avatarUrl: undefined,
    companyId: '1',
  },
  {
    id: 'sg-4',
    name: 'Team Delta',
    resourceIds: ['8', '9', '10'],
    avatarUrl: undefined,
    companyId: '3',
  },
  {
    id: 'sg-5',
    name: 'Team Epsilon',
    resourceIds: ['11', '12', '13'],
    avatarUrl: undefined,
    companyId: '4',
  },
  {
    id: 'sg-6',
    name: 'Team Zeta',
    resourceIds: ['14', '15', '16'],
    avatarUrl: undefined,
    companyId: '5',
  },
];
